import { createContext, useContext, useEffect, useRef, useState } from "react";
import { compareCompetitive } from "@/lib/competitiveSort";
import { CompeteChip } from "@/components/admission/CompeteChip";
import { CompetitiveReadingNote } from "@/components/admission/CompetitiveReadingNote";
import { SameScoreTag, sameScoreIds, groupedDisplayOrder } from "@/components/admission/SameScoreTag";
import { ShortlistSection } from "@/components/advanced/ShortlistSection";
import { PortfolioExplainer, SlotHeader, SlotEmptyReason, ShortlistedTag, shortlistedInSlot } from "@/components/advanced/PortfolioTransparency";

const SortModeContext = createContext({});
const MyCitiesContext = createContext(new Set());
const MATCH_INFLIGHT = new Map();
const cityKey = (c) => String(c || "").trim().toLowerCase();
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ChanceLegend from "@/components/app/ChanceLegend";
import BudgetFitChip from "@/components/app/BudgetFitChip";
import PswChip from "@/components/app/PswChip";
import { track, trackDebounced, EVENTS } from "@/lib/track";
import {
  Pencil, Sparkles, AlertTriangle, CheckCircle2, ShieldAlert,
  ExternalLink, Beaker, Plane, Briefcase, TrendingUp, Lock,
  Mail, Copy, Loader2, User, Bookmark, Check, ArrowRight, Sliders,
  Printer, Share2, HelpCircle,
} from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { useAuth } from "@/context/AuthContext";
import { useJourney } from "@/context/JourneyContext";
import { pushPendingShortlist } from "@/lib/pendingIntent";
import StudentLovedBadge from "@/components/app/StudentLovedBadge";
import NCBadge from "@/components/app/NCBadge";
import GradeConversionMissingBadge from "@/components/app/GradeConversionMissingBadge";
import TrustTierBadge from "@/components/app/TrustTierBadge";
import { useStudentLovedBulk } from "@/lib/useStudentLoved";
import AdvancedSearchLoader from "@/components/app/AdvancedSearchLoader";
import { MatchResultsSkeleton } from "@/components/app/MatchCardSkeleton";
import PlusCTA from "@/components/app/PlusCTA";
import PlusReportCompactStrip from "@/components/app/PlusReportCompactStrip";
import DirectoryMatchesSection from "@/components/app/DirectoryMatchesSection";
import ScholarshipEmailDialog from "@/components/app/advanced/ScholarshipEmailDialog";
import { CAODeadlineChip } from "@/components/app/CAODeadline";
import { UCASDeadlineChip } from "@/components/app/UCASDeadlineChip";
import { PTDeadlineChip } from "@/components/app/PTDeadlineChip";
import CurriculumAdmitBadge from "@/components/app/CurriculumAdmitBadge";
import PlacementsHintChip from "@/components/app/PlacementsHintChip";
import AboutMeCard, { IntlBadges } from "@/components/app/advanced/AboutMeCard";
import PersonalFitPopover from "@/components/app/advanced/PersonalFitPopover";
import BudgetEnvelope from "@/components/app/advanced/BudgetEnvelope";
import { stableHash, readCache, writeCache } from "@/lib/useCachedFetch";
import LEOVerifiedChip from "@/components/app/LEOVerifiedChip";
import VisaSponsorVerifiedChip from "@/components/app/VisaSponsorVerifiedChip";
import { useShortlistWriter } from "@/lib/useShortlistWriter";
import HesaSignalsStrip from "@/components/app/HesaSignalsStrip";
import InstitutionTypeFilter from "@/components/app/InstitutionTypeFilter";
import SubjectFilter from "@/components/app/SubjectFilter";
import FoundationYearPill from "@/components/app/FoundationYearPill";
import { trackEvent } from "@/lib/analytics";
import { formatEditorialOutcomes } from "@/lib/hesaSignals";
import { groupByUniversity } from "@/lib/groupByUniversity";
import { compareMatch, promoteShortlisted } from "@/lib/matchOrder";
import RefineBar from "@/components/app/RefineBar";
import { regionName } from "@/lib/regionNames";

const LAYER_LABELS = ["academic", "financial", "visa", "personal", "career"];

function LayerRow({ label, score, note, breakdown, testid, onAdjustWeights }) {
  const color = score >= 80 ? "text-emerald-700" : score >= 60 ? "text-amber-700" : "text-red-700";
  return (
    <div className="grid grid-cols-[110px_50px_1fr] items-start gap-3 py-1.5">
      <div className="label-uppercase !text-[10px] flex items-center gap-1">
        <span>{label}</span>
        {breakdown && (
          <PersonalFitPopover
            breakdown={breakdown}
            score={score}
            testid={testid}
            onAdjustWeights={onAdjustWeights}
          />
        )}
      </div>
      <div className={`font-mono font-semibold ${color}`}>{score}%</div>
      <div className="text-xs text-stone-600 leading-snug">{note}</div>
    </div>
  );
}

// PersonalFitPopover and BudgetEnvelope extracted (2026-02-04)
// → @/components/app/advanced/PersonalFitPopover, BudgetEnvelope

// Small chip rendered on each ResultCard next to the overall-match score,
// showing whether the total degree cost sits inside the user's budget
// envelope. Extracted to `@/components/app/BudgetFitChip` so the Shortlist
// page can reuse it.

function ScholarshipEmailDialogInline_REMOVED() { return null; } /* extracted to components/app/advanced/ScholarshipEmailDialog.jsx (2026-02-28) */

function ResultCard({ r, format, isFocused = false, saved = false, onAddToShortlist, satisfaction = null, onEditProfile, budgetEnvelope = 0, dreamEmployers = [], allUniIds = [], testIdPrefix = "match-card" }) {
  const acc = r.academic;
  const [draftFor, setDraftFor] = useState(null);
  const { competeMode, tiedIds } = useContext(SortModeContext);
  const myCities = useContext(MyCitiesContext);
  return (
    <Card data-testid={`${testIdPrefix}-${r.degree_id}`}
          data-focus-row={isFocused ? r.degree_id : undefined}
          className={`p-6 border-stone-200 bg-white lift ${isFocused ? "ring-2 ring-[#4C1D95] shadow-lg" : ""}`}>
      <div className="flex items-baseline justify-between">
        <div>
          <div className="label-uppercase">
            {r.country} · {r.city} · {r.duration_years}Y {r.qualification}
            {myCities.has(cityKey(r.city)) && (
              <span className="ml-2 rounded-full bg-[#F5F0FF] px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal text-[#7C3AED]" data-testid={`adv-mycity-${r.degree_id}`}>Your city</span>
            )}
          </div>
          <h3 className="font-editorial text-2xl mt-1 leading-tight">
            <Link to={`/degree/${r.degree_id}`} className="hover:underline underline-offset-4">{r.degree_name}</Link>
          </h3>
          {r.also_listed_as?.length > 0 && (
            <div data-testid={`adv-also-listed-${r.degree_id}`} className="mt-0.5 text-[11px] text-stone-500"
              title="The same course appears more than once in our catalogue. It is shown once, using the record with the most verified data; scores are not combined.">
              Also listed as: {r.also_listed_as.map((a) => `${a.degree_name}${a.qualification ? ` (${a.qualification}` : ""}${a.source_tier ? `${a.qualification ? ", " : " ("}${a.source_tier} record)` : a.qualification ? ")" : ""}`).join(" · ")}
            </div>
          )}
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            {competeMode && <CompeteChip r={r} testId={`adv-compete-chip-${r.degree_id}`} />}
            {competeMode && tiedIds?.has(r.degree_id) && <SameScoreTag r={r} testId={`adv-same-score-tag-${r.degree_id}`} />}
            <div className="text-xs text-stone-500">
              <Link to={`/uni/${r.university_id}`} className="hover:underline">{r.university_name}</Link>
            </div>
            <StudentLovedBadge
              stats={satisfaction}
              size="xs"
              testid={`student-loved-result-${r.degree_id}`}
            />
            <TrustTierBadge row={r} />
            <CurriculumAdmitBadge
              universityId={r.university_id}
              uniIdsInPage={allUniIds}
            />
            <FoundationYearPill show={r.foundation_year === true} />
            <NCBadge
              warnings={r.degree_warnings}
              testid={`nc-badge-result-${r.degree_id}`}
            />
            {acc?.grade_incomparable && (
              <GradeConversionMissingBadge
                compact
                applicantCurriculum={acc.applicant_curriculum}
              />
            )}
          </div>
          <HesaSignalsStrip
            signals={formatEditorialOutcomes(r.outcomes, format)}
            testIdPrefix={`match-${r.degree_id}-outcomes`}
          />
        </div>
        <div className="text-right shrink-0">
          <div className="font-chip text-4xl font-black text-[#4C1D95]">{r.overall_match}<span className="text-lg">%</span></div>
          <div className="label-uppercase !text-[9px]">Overall match</div>
          <div className="mt-2 flex justify-end flex-wrap gap-1">
            <PswChip years={r.international?.psw_eligible_years} />
            {budgetEnvelope > 0 && (
              <BudgetFitChip
                totalCost={r.financial?.total_degree_usd}
                netCost={r.financial?.net_degree_cost_usd}
                budget={budgetEnvelope}
                format={format}
              />
            )}
          </div>
        </div>
      </div>

      <IntlBadges intl={r.international} />

      {r.international?.sponsorship_prospects_note && (
        <p className="mt-2 text-[11px] text-stone-500 leading-snug italic" data-testid={`intl-note-${r.degree_id}`}>
          {r.international.sponsorship_prospects_note}
        </p>
      )}

      <div className="mt-4 border-t border-stone-100 pt-3">
        <LayerRow label="Academic" score={acc.score}
                  note={`${acc.label} — bucket: ${acc.portfolio_slot}`} />
        <LayerRow label="Financial" score={r.financial.score}
                  note={
                    r.financial.cost_incomplete
                      ? "Tuition not available: total cost incomplete"
                      : r.financial.total_scholarship_usd > 0
                      ? `Gross: ${format(r.financial.total_degree_usd)} · Scholarships: −${format(r.financial.total_scholarship_usd)} · Net: ${format(r.financial.net_degree_cost_usd)}`
                      : `Est. Year 1: ${format(r.financial.total_annual_usd)} · Total degree: ${format(r.financial.total_degree_usd)}`
                  } />
        <LayerRow label="Visa" score={r.visa.score}
                  note={`${r.visa.visa_name} · ${r.visa.processing_days_typical}-day processing`} />
        <LayerRow label="Personal" score={r.personal.score}
                  breakdown={r.personal.breakdown}
                  testid={`personal-fit-why-btn-${r.degree_id}`}
                  onAdjustWeights={onEditProfile}
                  note={
                    r.personal.warnings?.[0]
                      || r.personal.positives?.[0]
                      || (r.personal.score >= 75 ? "Matches your preferences well."
                          : r.personal.score >= 50 ? "Neutral fit against your preferences."
                          : "Weak match against your preferences.")
                  } />
        <LayerRow label="Career" score={r.career.score}
                  note={r.career.positives[0] || r.career.warnings[0] || "No career signals published for this course yet."} />
        {r.course_dna && (
          <LayerRow label="Course DNA" score={r.course_dna.score}
                    note={
                      r.course_dna.signals?.skipped
                        ? "Neutral — you didn't set any preferences."
                        : r.course_dna.signals?.missing
                        ? "Not yet published for this degree."
                        : (r.course_dna.positives?.[0] || r.course_dna.warnings?.[0] || "Teaching, workload and assessment fit your preferences.")
                    } />
        )}
      </div>

      {/* Dream-employer pipeline badge — high signal, low noise. Positive
          state shows the matched employer(s); negative state ("No confirmed
          pipeline") shows only when the user set a dream employer, so
          the visitor can eyeball which cards actually target it. */}
      {r.employer_pipeline?.confirmed ? (
        <div
          data-testid={`employer-pipeline-${r.degree_id}`}
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-[12px] text-emerald-900"
        >
          <Sparkles className="h-3.5 w-3.5 text-emerald-700" />
          <span className="font-medium">Feeds</span>
          <span>{r.employer_pipeline.matched.slice(0, 2).join(" · ")}</span>
          {r.employer_pipeline.matched.length > 2 && (
            <span className="text-emerald-700">+{r.employer_pipeline.matched.length - 2} more</span>
          )}
        </div>
      ) : (dreamEmployers.length > 0 && (
        <div
          data-testid={`employer-pipeline-none-${r.degree_id}`}
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-50 border border-stone-200 text-[12px] text-stone-600"
          title={`No graduates from this course list ${dreamEmployers.join(" or ")} in the destinations survey.`}
        >
          <span className="h-2 w-2 rounded-full bg-stone-400" aria-hidden />
          <span>No confirmed {dreamEmployers[0]}{dreamEmployers.length > 1 ? "+" : ""} pipeline</span>
        </div>
      ))}

      {/* LEO-verified salary chip — 5-year median from HMRC tax data.
          Only shows for UK degrees where we have coverage; other cards
          fall back to the estimated salary in the outcomes section. */}
      {(r.leo?.salary_5y?.p50 || r.visa_sponsor?.authority) && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {r.leo?.salary_5y?.p50 && (
            <LEOVerifiedChip leo={r.leo} testid={`leo-chip-${r.degree_id}`} />
          )}
          {r.visa_sponsor?.authority && (
            <VisaSponsorVerifiedChip
              sponsor={r.visa_sponsor}
              size="sm"
              testid={`visa-sponsor-chip-${r.degree_id}`}
            />
          )}
        </div>
      )}

      {/* Cohort-fit chips — one-liners like "Strong intl cohort (32%)". */}
      {(r.cohort_tags || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`cohort-tags-${r.degree_id}`}>
          {r.cohort_tags.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F5F1EA] border border-stone-300 text-[11px] text-stone-700">
              {t}
            </span>
          ))}
        </div>
      )}

      {/* CAO deadline chip — Irish degrees route through the CAO
          national platform with a fixed 1 Feb Round-1 close date, so
          surfacing it here (before the family clicks in) mirrors the
          UCAS-style deadline visibility UK cards already enjoy. */}
      <div className="mt-2 flex flex-wrap gap-1.5 items-center">
        <CAODeadlineChip degree={r} testid={`cao-deadline-chip-${r.degree_id}`} />
        <UCASDeadlineChip degree={r} testid={`ucas-deadline-chip-${r.degree_id}`} />
        <PTDeadlineChip degree={r} testid={`pt-deadline-chip-${r.degree_id}`} />
        <PlacementsHintChip degree={r} testid={`placements-hint-${r.degree_id}`} />
      </div>

      {r.financial.scholarships && r.financial.scholarships.length > 0 && (
        <details className="mt-3" data-testid={`scholarship-details-${r.degree_id}`}>
          <summary className="cursor-pointer text-xs text-[#4C1D95] hover:text-[#3B0764] font-medium">
            💰 {r.financial.scholarships.length} eligible scholarship{r.financial.scholarships.length > 1 ? "s" : ""} — {format(r.financial.total_scholarship_usd)} off net cost
          </summary>
          <ul className="mt-2 space-y-1.5 text-xs pl-2 border-l-2 border-violet-100">
            {r.financial.scholarships.map((s) => {
              // Only offer the drafter when the applicant needs to APPLY.
              const showDraft = !(s.auto_or_application || "").startsWith("automatic");
              return (
                <li key={s.name} data-testid={`scholarship-item-${r.degree_id}-${s.name}`}
                    className="text-stone-700">
                  <span className="font-medium text-stone-900">{s.name}</span>
                  <span className="ml-1 text-stone-500">
                    · {format(s.annual_value_usd)}/yr × {s.eligible_years}y = {format(s.total_value_usd)} total
                  </span>
                  {s.deadline && (
                    <span className="ml-1 text-stone-400 font-mono">· deadline {s.deadline}</span>
                  )}
                  {s.auto_or_application && (
                    <span className={`ml-1.5 text-[9px] uppercase tracking-wider ${
                      s.auto_or_application.startsWith("automatic")
                        ? "text-emerald-700"
                        : "text-amber-800"
                    }`}>
                      {s.auto_or_application.startsWith("automatic") ? "auto" : "apply"}
                    </span>
                  )}
                  {showDraft && (
                    <button
                      type="button"
                      onClick={() => setDraftFor(s.name)}
                      data-testid={`draft-email-btn-${r.degree_id}-${s.name}`}
                      className="ml-2 text-[10px] font-medium text-[#4C1D95] hover:text-[#3B0764] underline underline-offset-2 inline-flex items-center gap-1">
                      <Mail className="h-3 w-3" /> Draft email
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </details>
      )}

      <ScholarshipEmailDialog
        open={!!draftFor}
        onOpenChange={(v) => !v && setDraftFor(null)}
        degreeId={r.degree_id}
        degreeName={r.degree_name}
        universityName={r.university_name}
        scholarshipName={draftFor}
      />

      {r.next_action && (
        <div className="mt-4 pt-3 border-t border-stone-100">
          <div className="label-uppercase !text-[10px]">Your next action</div>
          <p className="text-sm text-stone-900 mt-1">{r.next_action}</p>
        </div>
      )}

      {(r.visa.warnings.length + r.financial.warnings.length + r.career.warnings.length) > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-stone-500 hover:text-stone-800">Show all warnings ({r.visa.warnings.length + r.financial.warnings.length + r.career.warnings.length})</summary>
          <ul className="mt-2 space-y-1 text-xs">
            {r.visa.warnings.map((w) => <li key={"v"+w} className="text-amber-800">· {w}</li>)}
            {r.financial.warnings.map((w) => <li key={"f"+w} className="text-amber-800">· {w}</li>)}
            {r.career.warnings.map((w) => <li key={"c"+w} className="text-amber-800">· {w}</li>)}
          </ul>
        </details>
      )}

      {r.support_factors.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {r.support_factors.slice(0, 3).map((s) => (
            <Badge key={s} variant="secondary" className="text-[10px] font-normal">{s}</Badge>
          ))}
        </div>
      )}

      {/* Actions — See course in detail + Add to shortlist */}
      <div
        data-testid={`match-card-actions-${r.degree_id}`}
        className="mt-5 pt-4 border-t border-stone-100 flex gap-2 flex-wrap"
      >
        <Link
          to={`/degree/${r.degree_id}`}
          data-testid={`see-course-btn-${r.degree_id}`}
          className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 bg-white hover:bg-stone-100 text-stone-900 text-sm px-3.5 py-2 transition-colors"
        >
          See course in detail <ArrowRight className="h-3.5 w-3.5" />
        </Link>
        <Button
          type="button"
          size="sm"
          onClick={() => onAddToShortlist?.(r)}
          disabled={saved}
          data-testid={`add-shortlist-btn-${r.degree_id}`}
          className={`text-sm px-3.5 py-2 h-auto ${
            saved
              ? "bg-emerald-700 hover:bg-emerald-700 text-white"
              : "bg-[#4C1D95] hover:bg-[#3B0764] text-white"
          }`}
        >
          {saved ? (
            <><Check className="h-4 w-4 mr-1.5" /> Saved to shortlist</>
          ) : (
            <><Bookmark className="h-4 w-4 mr-1.5" /> Add to shortlist</>
          )}
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// About Me — the applicant's full Advanced Search profile, retold in plain
// English and broken into 4 sections (Identity & mobility · Academic profile
// · Financial picture · Priorities). Every field the engine actually reads
// is surfaced so the user can see WHY the results look the way they do.
// ---------------------------------------------------------------------------
function KV({ label, value, testid }) {
  // Compact key/value cell — only renders when `value` is meaningful.
  if (value === null || value === undefined || value === "" ||
      (Array.isArray(value) && value.length === 0)) {
    return null;
  }
  return (
    <div data-testid={testid}>
      <div className="label-uppercase !text-[9px] text-stone-500">{label}</div>
      <div className="text-sm text-stone-900 mt-0.5">{value}</div>
    </div>
  );
}

function FitBar({ axis, value }) {
  const pct = Math.max(0, Math.min(10, Number(value) || 0)) * 10;
  return (
    <div className="flex items-center gap-3 text-xs">
      <div className="w-28 shrink-0 text-stone-700 capitalize">{axis}</div>
      <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
        <div className="h-full bg-stone-900" style={{ width: `${pct}%` }} />
      </div>
      <div className="w-8 shrink-0 text-right font-mono text-stone-600">{value}/10</div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <section data-testid={testid} className="border-t border-stone-200 pt-5 mt-5 first:border-0 first:pt-0 first:mt-0">
      <div className="label-uppercase text-stone-600">{title}</div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
        {children}
      </div>
    </section>
  );
}

export default function AdvancedResults({ profile, onEdit, onProfileSaved, focusDegreeId, anonymous = false, initialVerifiedOnly = false, flagshipOnly = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Hard-error state — set when the match call rejects (or when
  // `loading` finishes with `data` still null) so we can render a
  // retry UI instead of leaving the user staring at an infinite
  // "Running 5-layer match" loader when the backend is down or the
  // profile is empty.
  const [loadError, setLoadError] = useState(null);
  // Anonymous 3-match ceiling — when the backend returns 402
  // {code:"signup_required"} we flip this and render a full-page
  // auth wall over the results.
  const [anonWalled, setAnonWalled] = useState(false);
  const [activeTab, setActiveTab] = useState("best");
  const [excludedFilter, setExcludedFilter] = useState("all");
  const [savedIds, setSavedIds] = useState(new Set());
  const { addToShortlist: writeShortlist, removeFromShortlist: unwriteShortlist } = useShortlistWriter();
  const [assess, setAssess] = useState(null);
  const [assessLoading, setAssessLoading] = useState(false);
  const [assessError, setAssessError] = useState(false);
  const [fromCache, setFromCache] = useState(false);
  const [hideNC, setHideNC] = useState(false);
  // `onlyVerified` doubles up as the "Flagship-50 preset" toggle: when
  // ON, the backend pre-filters to trust-tier rows AND the ResultCard
  // list keeps only those rows (redundant but honest — the chip label
  // stays authoritative even if the server ever loosens the filter).
  const [onlyVerified, setOnlyVerified] = useState(Boolean(initialVerifiedOnly));
  // Only-UCAS-verified quick filter — surfaces the same
  // ucas_course_code_live_verified flag the DegreeDetail badge uses.
  // Client-side only (each result row already carries the boolean),
  // so no cache invalidation or match re-run needed on toggle. UK
  // rows only — non-UK results are always retained because UCAS is
  // a UK-only concept and dropping them would silently trash every
  // international shortlist.
  const [onlyUcasVerified, setOnlyUcasVerified] = useState(false);
  // Six-way sort for the flat results — mirrors the /admission
  // Quick Check dropdown so families get the same mental model on
  // both surfaces. `match` is the default and matches the historic
  // behaviour (overall_match DESC).
  const [advancedSort, setAdvancedSortState] = useState(() => {
    try { return localStorage.getItem("wua.advancedSort") || "match"; } catch { return "match"; }
  });
  const setAdvancedSort = (v) => {
    setAdvancedSortState(v);
    try { localStorage.setItem("wua.advancedSort", v); } catch { /* private mode */ }
  };
  const [cityFilter, setCityFilter] = useState(null);
  // "Only unis with confirmed pipeline to my dream employer(s)" — hard
  // filter used when the user arrives via a /how-to-get-into/{firm}
  // guide and wants to see ONLY courses that actually feed the firm,
  // not the soft-boost default. Auto-enabled when the URL carries
  // `?boost_career=1` so a visitor arriving from a firm CTA sees the
  // most focused list first; the highly-visible chip below tells them
  // exactly how many they're currently hiding so they never think
  // their earlier results have disappeared.
  const [onlyPipeline, setOnlyPipeline] = useState(
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("boost_career") === "1"
  );
  // 4 UK-only quick filters — leverage the HESA Phase-1/3 signals now
  // surfaced on each result row (tef_rating / nss_teaching_pct /
  // leo5_median_gbp / portfolio_required).
  const [onlyTefGold, setOnlyTefGold]         = useState(false);
  const [onlyPortfolio, setOnlyPortfolio]     = useState(false);
  const [onlyNss85, setOnlyNss85]             = useState(false);
  const [onlyLeo5High, setOnlyLeo5High]       = useState(false);
  // "≥5 curated placements" quick filter — surfaces the placement-depth
  // signal from the editorial `outcomes.top_employers` +
  // `outcomes.destinations.top_employers` union. Client-side only
  // (backend already ships `curated_placements_count` per row) so
  // toggling is instant.
  const [onlyPlacements5, setOnlyPlacements5] = useState(false);
  // Regional slicers — Spanish autonomous community / Italian region /
  // French region. Populated dynamically from the result set so the chips
  // only appear for countries currently represented in the shortlist.
  // Backed by URL search-params so a shared link ('?region=Cataluña') keeps
  // the filter applied for the recipient.
  const [urlParams, setUrlParams] = useSearchParams();
  const regionFilter = urlParams.get("region") || null;
  const setRegionFilter = (v) => {
    const next = new URLSearchParams(urlParams);
    if (v) next.set("region", v); else next.delete("region");
    setUrlParams(next, { replace: true });
  };
  // Italy — "Free tuition (ISEE < €22k)" chip. Simulates the No Tax Area
  // by showing only Italian degrees (whose EU cost families under the
  // reckonable-income threshold pay €0 for). Purely client-side; state
  // stored in URL alongside `region` so it persists on shared links.
  const freeTuitionItalyOn = urlParams.get("free_it") === "1";
  const setFreeTuitionItalyOn = (v) => {
    const next = new URLSearchParams(urlParams);
    if (v) next.set("free_it", "1"); else next.delete("free_it");
    setUrlParams(next, { replace: true });
  };
  // Budget envelope — total 3-year budget the user is willing to spend on
  // this degree. Defaults to their profile budget if set, otherwise $120k
  // (a common cost-sensitive envelope). Purely client-side; drives a
  // fits-budget chip on every ResultCard without re-ranking.
  const BUDGET_DEFAULT = 120000;
  const [budgetEnvelope, setBudgetEnvelope] = useState(
    Number(profile?.total_family_budget_usd) > 0
      ? Number(profile.total_family_budget_usd)
      : BUDGET_DEFAULT
  );
  // Layer weight tuner — lets the user re-rank instantly without a server
  // call. Defaults mirror the backend blend (0.25/0.20/0.15/0.13/0.15/0.12).
  const DEFAULT_LAYER_WEIGHTS = { academic: 25, financial: 20, visa: 15, personal: 13, career: 15, course_dna: 12 };
  // Career-boosted preset — used when the visitor arrived via a
  // /how-to-get-into/{firm} CTA (URL flag `?boost_career=1`). Cranks
  // career from 15% to ~32% and trims the other layers proportionally
  // so pipeline unis genuinely dominate the ranking, not just get a
  // small nudge.
  const CAREER_BOOST_WEIGHTS = { academic: 20, financial: 15, visa: 15, personal: 10, career: 32, course_dna: 8 };
  const initialWeights = urlParams.get("boost_career") === "1" && (profile?.dream_employers || []).length > 0
    ? CAREER_BOOST_WEIGHTS
    : DEFAULT_LAYER_WEIGHTS;
  const [layerWeights, setLayerWeights] = useState(initialWeights);
  const [tunerOpen, setTunerOpen] = useState(false);
  // Subject filter (frontend-only) — pill dropdown shown alongside the
  // Institution-type / Foundation-year chips whenever the applicant
  // picked more than one intended subject in the wizard. Empty means
  // "All subjects" (default); non-empty narrows both the visible
  // results and the excluded list to rows whose backend-attached
  // `matched_intended` tag includes the chosen chip. Declared here
  // (with the other filter useStates) so the hook order is stable
  // across the early-return branch below.
  const [subjectFilter, setSubjectFilter] = useState("");
  // Institution-type gate — powered by URL param `?inst=` so the
  // FE-college landing page can deep-link career-changers straight
  // into "just FE colleges". Passed to `/advanced/match` as a
  // query param and applied backend-side.
  const instFilter = urlParams.get("inst") || "";
  const setInstFilter = (v) => {
    const next = new URLSearchParams(urlParams);
    if (v) next.set("inst", v); else next.delete("inst");
    setUrlParams(next, { replace: true });
  };

  // Foundation-year filter — near-miss / mature-learner journey.
  // Set via `?level=foundation` on the URL so we can deep-link from
  // the FE-colleges landing / marketing pages.
  const foundationOnly = urlParams.get("level") === "foundation";
  const setFoundationOnly = (v) => {
    const next = new URLSearchParams(urlParams);
    if (v) next.set("level", "foundation"); else next.delete("level");
    setUrlParams(next, { replace: true });
    trackEvent("foundation_chip_toggle", { on: !!v });
  };

  // Firm slug lookup — needed so the "Compare these firms" shortcut in
  // the dream-employer banner can build a proper /employers/compare
  // URL from the dream_employers strings. One-shot fetch; cached in
  // component state.
  const [firmSlugMap, setFirmSlugMap] = useState({});
  useEffect(() => {
    if ((profile?.dream_employers || []).length < 2) return;
    (async () => {
      try {
        const { data: cur } = await api.get("/employers/curated");
        const m = {};
        for (const f of (cur.items || [])) {
          if (f.firm_name && f.slug) m[f.firm_name.toLowerCase()] = f.slug;
        }
        setFirmSlugMap(m);
      } catch { /* fall through — the compare link just won't show */ }
    })();
  }, [profile?.dream_employers?.length]);
  const dreamEmployerSlugs = (profile?.dream_employers || [])
    .map((n) => firmSlugMap[(n || "").toLowerCase()])
    .filter(Boolean);

  // Per-tab pagination — render the first PAGE_SIZE cards on every tab, then
  // reveal PAGE_SIZE more each time the user hits "Show more". Prevents a
  // 500-card DOM explosion (~15MB parsed, sluggish scroll) when the applicant's
  // profile matches half the catalogue. Data is already all in memory from one
  // /advanced/match call, so this is a pure render-cost optimisation.
  const PAGE_SIZE = 24;
  const [visibleCounts, setVisibleCounts] = useState({
    best: PAGE_SIZE,
    "lowest-risk": PAGE_SIZE,
    "best-career": PAGE_SIZE,
    excluded: PAGE_SIZE,
  });
  const showMore = (tab) =>
    setVisibleCounts((c) => ({ ...c, [tab]: (c[tab] || PAGE_SIZE) + PAGE_SIZE }));
  // Group-by-university toggle — mirrors the Quick Check surface. When
  // ON, each university collapses to a single flagship row (the top-
  // ranked variation per the active tab's sort) with a "N variations ▾"
  // chip that expands siblings inline. Defaults to ON because Advanced
  // Results very often returns 8+ variations per Russell Group uni and
  // the flat list buries the diversity.
  const [groupByUni, setGroupByUni] = useState(true);
  const [expandedUniGroups, setExpandedUniGroups] = useState(() => new Set());
  const toggleUniGroup = (uid) =>
    setExpandedUniGroups((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  const { user, login } = useAuth();
  const { pillarProgress, confirmPillar, anonymousId, profile: journeyProfile } = useJourney();
  const myCityNames = (journeyProfile?.where?.cities_confirmed || []).map((c) => c?.name || c?.label || c?.slug || c).filter(Boolean);
  const myCitySet = new Set(myCityNames.map(cityKey));
  const journeyHeaders = { headers: { "X-Journey-Anon-Id": anonymousId } };
  const { format } = useCurrency();

  // Bulk-fetch student satisfaction for every degree on the page so the
  // "Student-loved" badge renders without one HTTP request per card. Runs
  // whenever the results set changes. Hook must be declared here (before
  // the early return) to satisfy the Rules-of-Hooks.
  const satisfactionMap = useStudentLovedBulk(
    (data?.results || []).map((r) => r.degree_id)
  );

  // ---------------------------------------------------------------------------
  // Result cache — /advanced/match is a full 5-layer scoring pass across ~31k
  // degrees. Even at 200ms server-side it feels heavy to re-run when the user
  // just navigated away and came back. We stash the last result in
  // sessionStorage keyed by a hash of the profile that produced it. Any
  // profile edit invalidates the key automatically.
  // Cache key includes the trust-tier / flagship toggles so a
  // visitor arriving via `/advanced?preset=flagships` never gets
  // served a stale non-flagship cache entry (which surfaces as
  // "0 of 0 degrees" because the flagship pool doesn't intersect
  // a subject-scoped last-match). Same for `verified_only`.
  //
  // `instFilter` and `foundationOnly` MUST also be part of the key —
  // otherwise toggling the `Institution type` chip (or the foundation-
  // year chip) re-runs the useEffect below but `load()` hits the same
  // cache entry and re-hydrates the previous result set, so the chip
  // silently does nothing (and `Clear all` looks equally broken).
  const cacheKey = user
    ? `wua:advanced:${user.user_id || user.email}:${stableHash(profile)}` +
      `:v=${onlyVerified ? 1 : 0}:f=${flagshipOnly ? 1 : 0}` +
      `:i=${instFilter || ""}:fy=${foundationOnly ? 1 : 0}`
    : null;

  // Mark the journey's Prove stop done only from a fresh, successful match
  // (the server issues a receipt only when results came back).
  const recordProveDone = (resp) => {
    if (!resp?.journey_receipt || !resp.results?.length || pillarProgress?.advanced?.completed) return;
    confirmPillar("advanced", [{ receipt: resp.journey_receipt }]);
  };

  const loadReq = useRef(0);
  const load = async ({ force = false } = {}) => {
    // Cache hit — hydrate instantly and skip the API call.
    if (!force && cacheKey) {
      const cached = readCache(cacheKey);
      if (cached) {
        setData(cached);
        setLoading(false);
        setFromCache(true);
        return;
      }
    }
    setLoading(true);
    setLoadError(null);
    setFromCache(false);
    const myReq = ++loadReq.current;
    try {
      const params = new URLSearchParams();
      if (instFilter) params.set("institution_types", instFilter);
      if (foundationOnly) params.set("foundation_year_only", "true");
      if (onlyVerified) params.set("verified_only", "true");
      if (flagshipOnly) params.set("flagship_only", "true");
      if (anonymous) {
        // Anon path — POST profile body to /match-anon. Backend
        // enforces the 3-match ceiling per IP; a 402 flips the
        // auth wall.
        const url = params.toString()
          ? `/advanced/match-anon?${params.toString()}`
          : "/advanced/match-anon";
        try {
          const { data: resp } = await api.post(url, profile || {}, journeyHeaders);
          setData(resp);
          recordProveDone(resp);
        } catch (err) {
          if (err?.response?.status === 402) {
            setAnonWalled(true);
          } else {
            setLoadError(err?.response?.data?.detail || err?.message || "Could not run match");
            if (err?.response?.status !== 400) toast.error("Could not run match");
          }
        }
      } else {
        const url = params.toString()
          ? `/advanced/match?${params.toString()}`
          : "/advanced/match";
        // Share only an identical running search (same filters, profile and journey id), e.g. the sign-in re-render.
        const flight = `${url}|${stableHash(profile)}|${anonymousId}`;
        if (!MATCH_INFLIGHT.has(flight)) {
          MATCH_INFLIGHT.set(flight, api.post(url, null, journeyHeaders).finally(() => MATCH_INFLIGHT.delete(flight)));
        }
        const { data: resp } = await MATCH_INFLIGHT.get(flight);
        if (myReq !== loadReq.current) return;
        setData(resp);
        recordProveDone(resp);
        if (cacheKey) writeCache(cacheKey, resp);
      }
    } catch (err) {
      setLoadError(err?.response?.data?.detail || err?.message || "Could not run match");
      toast.error("Could not run match");
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [cacheKey, instFilter, foundationOnly, onlyVerified, flagshipOnly]);

  // Load the user's current shortlist so we can render "Saved to shortlist"
  // on cards that have already been saved. Kept as a Set for O(1) lookup.
  // Shortlist checked against this Advanced profile (same engine, nothing re-ranked).
  const assessSeq = useRef(0);
  const refreshShortlist = async () => {
    if (!user) return;
    const seq = ++assessSeq.current;
    setAssessLoading(true);
    setAssessError(false);
    try {
      const { data: a } = await api.post("/advanced/shortlist-assess");
      if (seq !== assessSeq.current) return;
      setAssess(a);
      setSavedIds(new Set((a.items || []).map((it) => it.degree_id).filter(Boolean)));
    } catch {
      if (seq !== assessSeq.current) return;
      setAssessError(true);
      try {
        const { data: sl } = await api.get("/shortlist");
        setSavedIds(new Set((sl.items || []).map((it) => it.degree_id).filter(Boolean)));
      } catch { /* silent — button will still work */ }
    } finally {
      if (seq === assessSeq.current) setAssessLoading(false);
    }
  };
  useEffect(() => {
    if (!user) return;
    void refreshShortlist();
    // Pick up shortlist changes made in another tab (Quick Check, /shortlist).
    const onFocus = () => { if (document.visibilityState === "visible") void refreshShortlist(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => { window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, cacheKey]);

  const removeFromShortlist = async (it) => {
    setAssess((a) => a && { ...a, items: a.items.filter((x) => !(x.university_id === it.university_id && x.degree_id === it.degree_id)) });
    setSavedIds((prev) => { const next = new Set(prev); next.delete(it.degree_id); return next; });
    try {
      await unwriteShortlist({ university_id: it.university_id, degree_id: it.degree_id });
      toast.success(`${it.degree_name || it.university_name || "Course"} removed from your shortlist`);
    } catch {
      toast.error("Could not remove. Please try again.");
    }
    void refreshShortlist();
  };

  const addToShortlist = async (r) => {
    if (!user) {
      pushPendingShortlist(r.university_id, r.university_name, r.degree_id);
      toast.info("Sign in to save — we'll add this course automatically.");
      login();
      return;
    }
    // Optimistic — flip the button state before the network settles.
    setSavedIds((prev) => new Set(prev).add(r.degree_id));
    try {
      await writeShortlist({
        university_id: r.university_id,
        degree_id: r.degree_id,
      });
      toast.success(`${r.degree_name} added to your shortlist`);
      void refreshShortlist();
    } catch {
      setSavedIds((prev) => {
        const next = new Set(prev);
        next.delete(r.degree_id);
        return next;
      });
      toast.error("Could not save. Please try again.");
    }
  };

  // Auto-scroll to the focused course when the results arrive and the
  // course is in the recommended list.
  useEffect(() => {
    if (!focusDegreeId || !data) return;
    const inResults = data.results.some((r) => r.degree_id === focusDegreeId);
    const inExcluded = (data.excluded || []).some((e) => e.degree_id === focusDegreeId);
    if (inExcluded && !inResults) {
      setActiveTab("excluded");
    }
    // Delay so the DOM has painted the tab content.
    setTimeout(() => {
      const el = document.querySelector(`[data-focus-row="${focusDegreeId}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
  }, [focusDegreeId, data]);

  // Anonymous auth-wall — the backend flipped `signup_required` after
  // the 3-match ceiling; render a hard interstitial that only offers
  // Sign in / Edit search. Nothing else on the page is available.
  if (anonWalled) {
    return (
      <div
        data-testid="anon-auth-wall"
        className="max-w-2xl mx-auto px-6 lg:px-10 py-16 text-center"
      >
        <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-[11px] font-underground-sc tracking-wider text-amber-900">
          <Lock className="h-3 w-3" /> Free trial used
        </div>
        <h2 className="mt-4 font-editorial text-4xl leading-tight text-[#002147]">
          You've used your 3 free matches
        </h2>
        <p className="mt-4 text-stone-700 leading-relaxed">
          Sign in to unlock unlimited matches, save your shortlist,
          re-run with new filters, and get a Professor summary explaining
          <em>why</em> each university is on your list.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Button
            data-testid="anon-wall-signin-btn"
            onClick={login}
            size="lg"
            className="bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-md"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Sign in for unlimited
          </Button>
          <Button
            data-testid="anon-wall-edit-btn"
            variant="outline"
            size="lg"
            onClick={() => (onEdit ? onEdit() : null)}
          >
            Edit my answers
          </Button>
        </div>
        <p className="mt-6 text-[11px] font-underground-sc tracking-wider text-stone-500">
          No credit card · we never sell your data
        </p>
      </div>
    );
  }

  if (loading) {
    // Combined "loading" state: a compact 5-layer stage strip explains what
    // the platform is computing, above a skeleton grid that mirrors the
    // eventual card layout. When data lands, cards paint in place with zero
    // layout shift.
    return (
      <div
        className="max-w-7xl mx-auto px-6 lg:px-10 py-10"
        data-testid="advanced-results-loading"
      >
        <AdvancedSearchLoader compact />
        <div className="mt-5">
          <MatchResultsSkeleton count={4} />
        </div>
      </div>
    );
  }

  // Loading finished but we still have no data — most commonly because the
  // match call errored (502, 500, timeout) or the profile is too empty for
  // the engine to score. Show an honest retry UI with an "Edit my answers"
  // escape hatch rather than leaving the user staring at the skeleton grid
  // forever.
  // The server asks for a subject when a search would otherwise scan most of the catalogue.
  const needsSubject = /add the course or subject/i.test(String(loadError || ""));
  if (!data && needsSubject) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="advanced-results-needs-subject">
        <div className="label-uppercase !text-[10px] text-[#4C1D95]">One more answer</div>
        <h2 className="font-editorial text-2xl sm:text-3xl mt-2 leading-tight text-[#002147]">
          Tell us which course or subject you're interested in.
        </h2>
        <p className="text-sm text-stone-600 mt-3 leading-relaxed">
          We couldn't find a course subject in your answers, and without one this search would cover too many courses to rank well. Pick one from the list (you can choose several) and we'll run your match.
        </p>
        {onEdit && (
          <Button className="mt-6 bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-md" data-testid="advanced-results-add-subject" onClick={() => onEdit()}>
            Add a subject
          </Button>
        )}
      </div>
    );
  }
  if (!data) {
    return (
      <div
        className="max-w-3xl mx-auto px-6 lg:px-10 py-16"
        data-testid="advanced-results-error"
      >
        <div className="label-uppercase !text-[10px] text-amber-700">We hit a snag</div>
        <h2 className="font-editorial text-2xl sm:text-3xl mt-2 leading-tight text-[#002147]">
          Couldn't run your 5-layer match.
        </h2>
        <p className="text-sm text-stone-600 mt-3 leading-relaxed">
          {loadError
            ? `The match engine returned: ${String(loadError).slice(0, 180)}`
            : "This usually clears up on a retry. If your profile is empty, add at least a curriculum and intended subject so the engine has something to score against."}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button
            data-testid="advanced-results-retry"
            onClick={() => load({ force: true })}
            className="bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-md"
          >
            <Loader2 className="h-4 w-4 mr-2" />
            Try again
          </Button>
          {onEdit && (
            <Button
              variant="outline"
              data-testid="advanced-results-edit"
              onClick={() => onEdit()}
            >
              Edit my answers
            </Button>
          )}
        </div>
      </div>
    );
  }

  const byId = Object.fromEntries(data.results.map((r) => [r.degree_id, r]));
  // Distinct uni_ids across the whole result set — passed down to
  // every ResultCard so <CurriculumAdmitBadge> can issue a single
  // bulk /admit-bands/bulk request instead of N (once per card).
  const allUniIds = Array.from(new Set((data.results || []).map((r) => r.university_id).filter(Boolean)));
  const subjectOptions = Array.isArray(data?.intended_degrees)
    ? data.intended_degrees.filter(Boolean)
    : [];
  const matchesSubjectFilter = (row) =>
    !subjectFilter || (row?.matched_intended || []).includes(subjectFilter);
  // NC filter (frontend-only) — client-side hide of any German course whose
  // degree_warnings carry `numerus_clausus`. Keeps summary counts intact so
  // the exclusion diagnostics remain honest — the chip only prunes the
  // rendered cards on the Best/Portfolio/Lowest-risk/Best-career tabs.
  const isNC = (r) =>
    Array.isArray(r?.degree_warnings)
    && r.degree_warnings.some((w) => w?.code === "numerus_clausus");
  // Verified-only filter — a record is "trust-tier" when the backend
  // stamps `content_verified: true` (manual admin audit of tuition /
  // offer_rate / entry_reqs / english_test against the university's
  // official admissions page) OR carries `source_tier: "editorial"`
  // (hand-curated flagship seed — NL WO-14, FR Top-12, etc). Applicants
  // building a final shortlist want to see only rows we'd stand behind
  // at a college fair.
  const isVerified = (r) => (
    r?.content_verified === true
    || r?.source_tier === "editorial"
  );
  // Refine filters — plain-language, each with a live count of what would be left.
  const toggleOf = (setter) => () => setter((v) => !v);
  const isUkRow = (r) => r?.country === "UK" || (r?.university_id || "").startsWith("uk-");
  const filterDefs = [
    { key: "teaching", group: "main", label: "Highly rated teaching", on: onlyNss85, toggle: toggleOf(setOnlyNss85),
      hint: "UK courses where at least 85% of students rated the teaching positively (National Student Survey 2025)",
      test: (r) => (r?.nss_teaching_pct ?? 0) >= 85 },
    { key: "foundation", group: "main", label: "Foundation year route", on: foundationOnly, toggle: () => setFoundationOnly(!foundationOnly),
      hint: "Courses you can start with a foundation year if your grades fall a little short",
      test: (r) => r?.foundation_year === true },
    { key: "portfolio", group: "main", label: "Portfolio required", on: onlyPortfolio, toggle: toggleOf(setOnlyPortfolio),
      hint: "Courses that ask for a portfolio of your work as part of the application",
      test: (r) => r?.portfolio_required === true },
    { key: "checked", group: "main", label: "Details checked by us", on: onlyVerified, toggle: toggleOf(setOnlyVerified),
      hint: "Fees, entry requirements and offer rates checked by our team against the university's own admissions page",
      test: (r) => isVerified(r) },
    { key: "gov_rating", group: "main", label: "Top government teaching rating", on: onlyTefGold, toggle: toggleOf(setOnlyTefGold),
      hint: "UK universities rated Gold in the government's Teaching Excellence Framework",
      test: (r) => r?.tef_rating === "Gold" },
    { key: "ucas", group: "more", label: "Course code confirmed with UCAS", on: onlyUcasVerified, toggle: toggleOf(setOnlyUcasVerified),
      hint: "UK courses whose UCAS course code we've confirmed is live. Courses outside the UK stay in the list.",
      test: (r) => r?.ucas_course_code_live_verified === true || !isUkRow(r) },
    { key: "salary", group: "more", label: "Graduates earn £50k+ after 5 years", on: onlyLeo5High, toggle: toggleOf(setOnlyLeo5High),
      hint: "Typical (median) salary five years after graduating, from UK government tax records",
      test: (r) => (r?.leo5_median_gbp ?? 0) >= 50000 },
    { key: "employers", group: "more", label: "5+ named graduate employers", on: onlyPlacements5, toggle: toggleOf(setOnlyPlacements5),
      hint: "Courses where we can name at least five employers that hire their graduates",
      test: (r) => (r?.curated_placements_count ?? 0) >= 5 },
    { key: "no_nc", group: "more", label: "Hide German capped-entry subjects", on: hideNC, toggle: toggleOf(setHideNC),
      hint: "Hides German courses with a strict cap on places (e.g. Medicine, Psychology, Law), where only around 20% of places go to non-EU applicants",
      test: (r) => !isNC(r) },
    { key: "free_it", group: "more", label: "Free tuition in Italy (family income under €22k)", on: freeTuitionItalyOn,
      toggle: () => setFreeTuitionItalyOn(!freeTuitionItalyOn),
      hint: "Italian public universities, where families with an ISEE income indicator under €22,000 pay no tuition",
      test: (r) => r?.country === "Italy" && r?.university_id !== "it-bocconi" && r?.university_id !== "it-luiss" },
  ];
  const pipelineDef = { key: "pipeline", on: onlyPipeline, test: (r) => r?.employer_pipeline?.confirmed === true };
  const regionOf = (r) => r?.autonomous_community || r?.region;
  const activeCity = cityFilter && myCitySet.has(cityFilter) ? cityFilter : null;
  const passesFilters = (r, skip) => matchesSubjectFilter(r)
    && (skip === "city" || !activeCity || cityKey(r.city) === activeCity)
    && (skip === "region" || !regionFilter || regionOf(r) === regionFilter)
    && [...filterDefs, pipelineDef].every((f) => f.key === skip || !f.on || f.test(r));
  const visibleResultsRaw = data.results.filter((r) => passesFilters(r));
  for (const f of filterDefs) {
    f.count = data.results.filter((r) => passesFilters(r, f.key) && f.test(r)).length;
    f.hidden = !f.on && (f.count === 0 || f.count === visibleResultsRaw.length);  // never offer a dead or no-op filter
  }
  const regionOptions = (() => {
    const counts = new Map();
    const countryOf = new Map();
    for (const r of data.results) {
      const k = regionOf(r);
      if (k && passesFilters(r, "region")) {
        counts.set(k, (counts.get(k) || 0) + 1);
        if (!countryOf.has(k)) countryOf.set(k, r.country);
      }
    }
    const multiCountry = new Set(countryOf.values()).size > 1;
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([name, count]) => {
      const full = regionName(name, countryOf.get(name));
      return { name, count, label: multiCountry && countryOf.get(name) ? `${full} · ${countryOf.get(name)}` : full };
    });
  })();
  const anyRefineActive = filterDefs.some((f) => f.on) || Boolean(regionFilter) || Boolean(subjectFilter) || Boolean(instFilter);
  const clearAllRefine = () => {
    [setOnlyNss85, setOnlyPortfolio, setOnlyVerified, setOnlyTefGold, setOnlyUcasVerified, setOnlyLeo5High,
      setOnlyPlacements5, setHideNC].forEach((set) => set(false));
    setSubjectFilter("");
    const next = new URLSearchParams(urlParams);
    ["region", "free_it", "level", "inst"].forEach((k) => next.delete(k));
    setUrlParams(next, { replace: true });
  };
  // Subject-filtered excluded list — keeps the Excluded tab in sync with
  // the visible shortlist when a specific subject chip is active.
  // subject_not_offered rows carry `matched_intended: []` so they drop
  // out under any specific chip filter (they by definition don't match
  // the chip), and re-appear under "All subjects".
  const excludedForSubject = subjectFilter
    ? (data.excluded || []).filter(matchesSubjectFilter)
    : (data.excluded || []);
  // Client-side re-rank + re-sort when the user tunes layer weights.
  // Bypassed when weights match defaults (identity map) so the ordering
  // the backend picked wins by default.
  const isDefaultWeights = Object.keys(DEFAULT_LAYER_WEIGHTS).every(
    (k) => layerWeights[k] === DEFAULT_LAYER_WEIGHTS[k],
  );
  // Server order is Best match, so default weights + Best match keeps it as-is; any other sort always applies.
  const sortMode = advancedSort === "mycities" && !myCityNames.length ? "match" : advancedSort;
  const visibleResults = isDefaultWeights && sortMode === "match"
    ? visibleResultsRaw
    : (() => {
        const wSum = Object.values(layerWeights).reduce((a, b) => a + Math.max(0, b), 0) || 1;
        const recompute = (r) => Math.round(
          (Math.max(0, layerWeights.academic) / wSum) * (r.academic?.score || 0) +
          (Math.max(0, layerWeights.financial) / wSum) * (r.financial?.score || 0) +
          (Math.max(0, layerWeights.visa) / wSum) * (r.visa?.score || 0) +
          (Math.max(0, layerWeights.personal) / wSum) * (r.personal?.score || 0) +
          (Math.max(0, layerWeights.career) / wSum) * (r.career?.score || 0) +
          (Math.max(0, layerWeights.course_dna) / wSum) * (r.course_dna?.score || 55)
        );
        // Coarse tie-breakers reused across sort modes.
        const tierRank = { editorial: 0, verified: 1, directory: 2 };
        const bottomOf = (v) => (v == null || Number.isNaN(v));
        const enriched = isDefaultWeights ? visibleResultsRaw : visibleResultsRaw.map((r) => ({
          ...r,
          overall_match: recompute(r),
        }));
        switch (sortMode) {
          case "safest":
            // Highest academic score first (best-fit-for-my-grades).
            // On the /advanced side we have a proper academic layer
            // score rather than the /admission gap_score, so a single
            // DESC sort captures the "safest bet" intent.
            return [...enriched].sort((a, b) =>
              (b.academic?.score ?? 0) - (a.academic?.score ?? 0)
            );
          case "competitive":
            // Entry-competitiveness — UK by typical UCAS tariff,
            // others by acceptance rate. Rows without either signal
            // (`competitiveness_source == null`) sink to the bottom
            // ordered by overall_match so the top of the list is
            // always a genuine competitiveness ranking, never a
            // silent fallback that masquerades as one.
            return [...enriched].sort(compareCompetitive);
          case "value": {
            // overall_match / total 3-year cost. Missing tuition sinks.
            const yearCost = (r) => r.tuition_intl_annual_usd || 0;
            const yrs = (r) => r.degree_years_typical || 3;
            const val = (r) => {
              const c = yearCost(r) * yrs(r);
              return c > 0 ? r.overall_match / c : -1;
            };
            return [...enriched].sort((a, b) => val(b) - val(a));
          }
          case "salary":
            return [...enriched].sort((a, b) => {
              const av = a.avg_starting_salary_usd, bv = b.avg_starting_salary_usd;
              if (bottomOf(av) && bottomOf(bv)) return b.overall_match - a.overall_match;
              if (bottomOf(av)) return 1;
              if (bottomOf(bv)) return -1;
              return bv - av;
            });
          case "acceptance":
            return [...enriched].sort((a, b) => {
              const av = a.acceptance_rate, bv = b.acceptance_rate;
              if (bottomOf(av) && bottomOf(bv)) return b.overall_match - a.overall_match;
              if (bottomOf(av)) return 1;
              if (bottomOf(bv)) return -1;
              return bv - av;
            });
          case "alpha":
            return [...enriched].sort((a, b) =>
              (a.university_name || "").localeCompare(b.university_name || "")
            );
          case "placements":
            // Placement density first — the "pipeline strength" sort.
            // Rows with 0/undef curated placements sink to the bottom
            // ordered by overall_match so a family scanning the top of
            // the list sees only unis with named graduate employers.
            return [...enriched].sort((a, b) => {
              const ap = a.curated_placements_count ?? 0;
              const bp = b.curated_placements_count ?? 0;
              if (ap === bp) return b.overall_match - a.overall_match;
              return bp - ap;
            });
          case "mycities":
            // Courses in the student's confirmed cities first; each part keeps the normal match order.
            return [...enriched].sort((a, b) =>
              (myCitySet.has(cityKey(b.city)) - myCitySet.has(cityKey(a.city))) || compareMatch(a, b));
          case "match":
          default:
            return [...enriched].sort(compareMatch);
        }
      })();

  // ── Focus resolution ────────────────────────────────────────────────
  // When the user clicked "Score this course on all 5 layers" on the
  // Quick-Check narrative, they arrived here with ?focus=<degree_id>.
  // Show a targeted banner explaining how that specific course scored
  // — INCLUDED (with layer results) or EXCLUDED (with the reason).
  let focusRow = null;
  let focusExcluded = null;
  if (focusDegreeId) {
    focusRow = byId[focusDegreeId] || null;
    if (!focusRow) {
      focusExcluded = (data.excluded || []).find((e) => e.degree_id === focusDegreeId) || null;
    }
  }

  /**
   * renderGroupedResultCards — render helper shared by the Best overall,
   * Lowest visa risk and Best career return tabs. When `groupByUni` is
   * on, collapses variations under the flagship (first-in-sort per uni)
   * and inserts a "N variations ▾" chip that expands siblings inline.
   * Preserves the sort's intent because the first occurrence of each
   * uni_id in the pre-sorted input IS the top-ranked variation.
   *
   * Returns { node, remaining } where `remaining` counts groups (or
   * rows, when the toggle is off) still hidden by the pagination
   * ceiling.
   */
  const renderGroupedResultCards = (rows, tabKey, visible) => {
    if (!groupByUni) {
      const shown = rows.slice(0, visible);
      return {
        node: (
          <div className="grid md:grid-cols-2 gap-6">
            {shown.map((r) => (
              <ResultCard
                key={r.degree_id}
                r={r}
                format={format}
                isFocused={r.degree_id === focusDegreeId}
                saved={savedIds.has(r.degree_id)}
                onAddToShortlist={addToShortlist}
                satisfaction={satisfactionMap[r.degree_id]}
                onEditProfile={onEdit}
                budgetEnvelope={budgetEnvelope}
                dreamEmployers={profile?.dream_employers || []}
                allUniIds={allUniIds}
              />
            ))}
          </div>
        ),
        remaining: rows.length - shown.length,
        unit: "course",
        unitPlural: "courses",
      };
    }
    const grouped = groupByUniversity(rows);
    const groups = tabKey === "best" && sortMode === "match" ? promoteShortlisted(grouped, savedIds) : grouped;
    const sliceGroups = groups.slice(0, visible);
    // Single-column layout when grouping is on. A 2-col grid produces
    // orphan gaps whenever a flagship-with-variations (full-width)
    // follows a flagship-without-variations (half-width). Stacking
    // vertically also reads as one "uni cluster" per row which
    // matches the mental model of the toggle.
    const nodes = [];
    sliceGroups.forEach((g) => {
      const uid = g.university_id;
      const isExpanded = uid && expandedUniGroups.has(uid);
      const hasVariations = g.variations.length > 0;
      nodes.push(
        <ResultCard
          key={g.flagship.degree_id}
          r={g.flagship}
          format={format}
          isFocused={g.flagship.degree_id === focusDegreeId}
          saved={savedIds.has(g.flagship.degree_id)}
          onAddToShortlist={addToShortlist}
          satisfaction={satisfactionMap[g.flagship.degree_id]}
          onEditProfile={onEdit}
          budgetEnvelope={budgetEnvelope}
          dreamEmployers={profile?.dream_employers || []}
          allUniIds={allUniIds}
        />
      );
      if (hasVariations) {
        nodes.push(
          <div key={`${uid}-chip-${tabKey}`} className="-mt-2">
            <button
              type="button"
              data-testid={`advresults-variations-toggle-${tabKey}-${uid}`}
              onClick={() => toggleUniGroup(uid)}
              aria-expanded={isExpanded}
              className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white px-2.5 py-0.5 text-[10px] font-underground-sc tracking-wider text-stone-600 hover:border-[#4C1D95] hover:text-[#4C1D95] transition-colors"
            >
              {g.variations.length} variation{g.variations.length === 1 ? "" : "s"} at this uni
              <span className="ml-0.5">{isExpanded ? "▴" : "▾"}</span>
            </button>
          </div>
        );
        if (isExpanded) {
          g.variations.forEach((child) => {
            nodes.push(
              <div
                key={`${child.degree_id}-var`}
                className="pl-6 border-l-2 border-stone-200 ml-2"
              >
                <ResultCard
                  r={child}
                  format={format}
                  saved={savedIds.has(child.degree_id)}
                  onAddToShortlist={addToShortlist}
                  satisfaction={satisfactionMap[child.degree_id]}
                  onEditProfile={onEdit}
                  budgetEnvelope={budgetEnvelope}
                  dreamEmployers={profile?.dream_employers || []}
                  allUniIds={allUniIds}
                />
              </div>
            );
          });
        }
      }
    });
    return {
      node: <div className="flex flex-col gap-6">{nodes}</div>,
      remaining: groups.length - sliceGroups.length,
      unit: "university",
      unitPlural: "universities",
    };
  };

  const shortlistHiddenReason = (row) => {
    if (!data.results.some((r) => r.degree_id === row.degree_id)) return "it's outside this search's institution-type, flagship or checked-details filter.";
    const why = filterDefs.filter((f) => f.on && !f.test(row)).map((f) => `"${f.label}"`);
    if (onlyPipeline && !pipelineDef.test(row)) why.push('"employer pipeline"');
    if (!matchesSubjectFilter(row)) why.push("the subject filter");
    if (regionFilter && regionOf(row) !== regionFilter) why.push("the region filter");
    return why.length ? `hidden by ${why.join(", ")}. Turn ${why.length === 1 ? "it" : "them"} off to see it.` : null;
  };
  const competeMode = advancedSort === "competitive" && activeTab === "best";
  const showShortlistTab = Boolean(user && !anonymous);
  const shortlistCount = assess?.items ? assess.items.length : savedIds.size;
  const TAB_CLS = "data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-stone-900 rounded-none -mb-px px-2 py-2";
  const tiedIds = competeMode
    ? sameScoreIds(groupByUni ? groupedDisplayOrder(groupByUniversity(visibleResults), (uid) => uid && expandedUniGroups.has(uid)) : visibleResults, (r) => r.degree_id)
    : new Set();

  return (
    <SortModeContext.Provider value={{ competeMode, tiedIds }}>
    <MyCitiesContext.Provider value={myCitySet}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      {anonymous && data?.anon_meta && (
        <div
          data-testid="anon-usage-banner"
          className="mb-6 rounded-md border border-amber-300 bg-amber-50/60 px-4 py-3 flex flex-wrap items-center justify-between gap-3"
        >
          <div className="text-[13px] text-stone-800 leading-snug">
            <span className="font-mono font-semibold text-amber-900">
              Free trial · {data.anon_meta.used} of {data.anon_meta.limit} matches used.
            </span>{" "}
            {data.anon_meta.remaining > 0
              ? `${data.anon_meta.remaining} left — sign in to unlock unlimited runs & save this shortlist.`
              : "This is your last free run. Sign in now to keep this shortlist and re-run whenever your grades change."}
          </div>
          <Button
            data-testid="anon-banner-signin-btn"
            onClick={login}
            size="sm"
            className="bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-md"
          >
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            Sign in for unlimited
          </Button>
        </div>
      )}
      {(profile?.dream_employers || []).length > 0 && (
        <div
          data-testid="results-dream-employer-banner"
          className={`mb-6 rounded-md border px-4 py-3 ${
            onlyPipeline
              ? "border-emerald-500 bg-emerald-100/70 ring-1 ring-emerald-400"
              : "border-emerald-300 bg-emerald-50/60"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-[13px] text-stone-800 leading-snug">
              <span className="font-mono font-semibold text-emerald-900">
                Targeting: {(profile.dream_employers || []).join(" · ")}
              </span>
              {" · "}
              {onlyPipeline ? (
                <>
                  Filter active — only universities with a confirmed pipeline
                  are shown below.
                </>
              ) : (
                <>
                  Universities with a confirmed pipeline get a career-fit boost.
                  Look for the{" "}
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 border border-emerald-300 text-emerald-800 font-mono text-[10px] uppercase tracking-wider">
                    <Briefcase className="h-2.5 w-2.5" /> Feeds
                  </span>
                  {" "}chip on result cards below.
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="results-only-pipeline-toggle"
                onClick={() => setOnlyPipeline((v) => !v)}
                aria-pressed={onlyPipeline}
                className={`text-[11px] font-underground-sc tracking-wider rounded-full border px-3 py-1.5 transition ${
                  onlyPipeline
                    ? "bg-emerald-700 text-white border-emerald-700"
                    : "bg-white text-emerald-900 border-emerald-300 hover:border-emerald-700"
                }`}
              >
                {onlyPipeline ? "✓ Pipeline unis only" : "Show only pipeline unis"}
              </button>
              <Button
                data-testid="results-dream-employer-edit"
                variant="outline"
                size="sm"
                onClick={onEdit}
                className="rounded-md"
              >
                Change target
              </Button>
            </div>
          </div>
          {dreamEmployerSlugs.length >= 2 && (
            <div className="mt-2 text-[12px] text-stone-700">
              <Link
                to={`/employers/compare?slugs=${dreamEmployerSlugs.join(",")}`}
                data-testid="results-compare-firms-link"
                className="text-[#4C1D95] underline underline-offset-2 hover:text-emerald-800"
              >
                Compare {dreamEmployerSlugs.length} firms side-by-side →
              </Link>
              <span className="ml-2 text-stone-500 text-[11px]">
                salary bands, feeder unis and cohort progression across
                {" "}{profile.dream_employers.join(" vs ")}.
              </span>
            </div>
          )}
          {onlyPipeline && (
            <div
              data-testid="results-pipeline-hidden-notice"
              className="mt-3 pt-3 border-t border-emerald-300/60 flex flex-wrap items-center justify-between gap-2 text-[12px] text-emerald-900"
            >
              <span>
                <span className="font-mono font-semibold">
                  {(data.results || []).filter((r) => !(r?.employer_pipeline?.confirmed)).length}
                </span>{" "}
                of your{" "}
                <span className="font-mono font-semibold">
                  {(data.results || []).length}
                </span>{" "}
                matches are hidden by this filter — they didn't name{" "}
                {(profile.dream_employers || []).slice(0, 2).join(" or ")} in the destinations survey.
              </span>
              <button
                type="button"
                data-testid="results-show-all-hidden"
                onClick={() => setOnlyPipeline(false)}
                className="rounded-full border border-emerald-400 bg-white px-3 py-1 text-[11px] font-underground-sc tracking-wider text-emerald-800 hover:bg-emerald-50"
              >
                Show all matches
              </button>
            </div>
          )}
        </div>
      )}
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="label-uppercase">Advanced Search · Results</div>
          <h1 className="font-editorial text-4xl mt-2">Degrees that fit you.</h1>
          <p className="mt-2 text-stone-600 text-sm" data-testid="results-summary">
            {data.summary.recommended} of {data.summary.reviewed} degrees recommended
            {data.summary.exclusion_counts && Object.keys(data.summary.exclusion_counts).length > 0 && (
              <> · {Object.entries(data.summary.exclusion_counts).map(([cat, n], i, arr) => (
                <span key={cat}>
                  <strong className="text-stone-800">{n}</strong>{" "}
                  {cat === "subject_not_offered" && "on subject"}
                  {cat === "gate" && "on visa/financial gate"}
                  {cat === "no_sponsor_licence" && "no sponsor licence"}
                  {i < arr.length - 1 && ", "}
                </span>
              ))}</>
            )}
            {(!data.summary.exclusion_counts || Object.keys(data.summary.exclusion_counts).length === 0) &&
              <> · {data.summary.excluded} excluded on gates.</>
            }
          </p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button
            variant="outline"
            onClick={() => { window.print(); }}
            data-testid="advanced-print-btn"
            className="rounded-md"
          >
            <Printer className="h-4 w-4 mr-1" /> Print / PDF
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const url = window.location.href;
                if (navigator.share) {
                  try { await navigator.share({ title: "My university shortlist", url }); return; } catch {}
                }
                await navigator.clipboard.writeText(url);
                toast.success("Link copied. Recipients need to sign in to view — public share links coming soon.");
              } catch {
                toast.error("Could not copy the link.");
              }
            }}
            data-testid="advanced-share-btn"
            className="rounded-md"
          >
            <Share2 className="h-4 w-4 mr-1" /> Share
          </Button>
        </div>
      </div>

      {/* Six-way sort dropdown — mirrors the /admission Quick Check
          "Your buckets" control so families get the same mental model
          on both surfaces. Client-side, no re-fetch. */}
      <div
        data-testid="advanced-sort-control"
        className="mt-4 flex flex-wrap items-center gap-2 print:hidden"
      >
        <label htmlFor="advanced-sort" className="text-[10px] font-underground-sc tracking-wider text-stone-500">
          Sort by
        </label>
        <select
          id="advanced-sort"
          data-testid="advanced-sort-dropdown"
          value={sortMode}
          onChange={(e) => setAdvancedSort(e.target.value)}
          className="text-[11px] font-underground-sc tracking-wider bg-white border border-stone-300 rounded-md px-2 py-1 hover:border-[#4C1D95] focus:outline-none focus:border-[#4C1D95] cursor-pointer"
        >
          <option value="match">Best match (default)</option>
          <option value="competitive">Most competitive first</option>
          <option value="safest">Safest bets first</option>
          <option value="value">Best value (match ÷ cost)</option>
          <option value="salary">Highest graduate salary</option>
          <option value="acceptance">Highest acceptance rate</option>
          <option value="placements">Most placements (pipeline first)</option>
          <option value="alpha">A → Z alphabetical</option>
          {myCityNames.length > 0 && <option value="mycities">My cities first</option>}
        </select>
        {/* Group-by-university toggle — collapses each uni into a
            flagship row + expandable "N variations ▾" chip. Applies to
            the Best overall / Lowest visa risk / Best career return
            tabs. Balanced portfolio slots are already deliberately
            hand-picked (2/3/3/2), so the toggle skips them. */}
        <button
          type="button"
          data-testid="advresults-group-by-uni-toggle"
          onClick={() => setGroupByUni((v) => !v)}
          aria-pressed={groupByUni}
          className={
            "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-underground-sc tracking-wider transition-colors " +
            (groupByUni
              ? "border-[#4C1D95] bg-[#4C1D95] text-white hover:bg-[#3a1476]"
              : "border-stone-300 bg-white text-stone-700 hover:border-[#4C1D95]")
          }
          title="Collapse each university into one flagship course; click a chip to reveal sibling courses at the same uni."
        >
          Group by uni
        </button>
        {sortMode === "mycities" && (
          <span data-testid="advanced-sort-mycities-hint" className="text-[11px] text-stone-600 leading-snug">
            {visibleResults.filter((r) => myCitySet.has(cityKey(r.city))).length} courses in your cities ({myCityNames.join(", ")}) first, then the rest. Match scores are unchanged.
          </span>
        )}
        {advancedSort === "safest" && (
          <span
            data-testid="advanced-sort-safest-hint"
            className="text-[11px] text-stone-600 leading-snug"
          >
            "Safest bets" ranks by academic-layer fit — not overall fit. Less-selective unis surface first by design.
          </span>
        )}
        {advancedSort === "competitive" && (() => {
          const missing = visibleResults.filter((r) => r.competitiveness_index == null).length;
          const subjectCount = visibleResults.filter((r) => r.subject_offer_rate != null).length;
          const pct = visibleResults.length
            ? Math.round((missing / visibleResults.length) * 100)
            : 0;
          return (
            <span
              data-testid="advanced-sort-competitive-hint"
              className="text-[11px] text-stone-600 leading-snug"
              title="Sorted by entry competitiveness — UK courses blend HESA typical UCAS tariff (40%) with 1 − acceptance rate (60%). Others use acceptance rate alone. Not a league-table ranking."
            >
              Sorted by entry competitiveness — UK blends UCAS tariff with acceptance rate; others use acceptance rate. Not a league-table ranking.
              {subjectCount > 0 && (
                <> <span className="text-indigo-800">{subjectCount} use subject-precise UCAS offer rates</span>; the rest use uni-average acceptance.</>
              )}
              {missing > 0 && (
                <> <span className="text-amber-800">{missing} of {visibleResults.length} ({pct}%) sink for lack of a signal.</span></>
              )}
            </span>
          );
        })()}
        {(advancedSort === "salary" || advancedSort === "acceptance") && (() => {
          const key = advancedSort === "salary" ? "avg_starting_salary_usd" : "acceptance_rate";
          const missing = visibleResults.filter((r) => r[key] == null).length;
          if (!missing) return null;
          const pct = Math.round((missing / visibleResults.length) * 100);
          return (
            <span
              data-testid="advanced-sort-coverage-hint"
              className="text-[11px] text-amber-800 leading-snug"
            >
              Sparse data: {missing} of {visibleResults.length} ({pct}%) lack this signal — those sink to the bottom.
            </span>
          );
        })()}
      </div>
      {myCityNames.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 print:hidden" data-testid="advanced-city-chips">
          <span className="text-[10px] font-underground-sc tracking-wider text-stone-500">Your cities</span>
          {[null, ...myCityNames].map((name) => {
            const k = name ? cityKey(name) : null;
            const on = activeCity === k;
            const n = name ? data.results.filter((r) => passesFilters(r, "city") && cityKey(r.city) === k).length : null;
            return (
              <button key={name || "all"} type="button" aria-pressed={on} onClick={() => setCityFilter(k)}
                data-testid={`advanced-city-chip-${name ? k.replace(/[^a-z0-9]+/g, "-") : "all"}`}
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors ${on ? "border-[#7C3AED] bg-[#7C3AED] text-white" : "border-stone-300 bg-white text-stone-700 hover:border-[#7C3AED]"}`}>
                {name ? <>{name} <span className={on ? "text-white/80" : "text-stone-400"}>{n}</span></> : "All places"}
              </button>
            );
          })}
        </div>
      )}
      {advancedSort === "competitive" && activeTab === "best" && (
        <div className="mt-3 print:hidden"><CompetitiveReadingNote results={visibleResults} /></div>
      )}

      {/* About Me — human-readable retelling of the profile */}
      <AboutMeCard profile={profile} onEdit={onEdit} format={format} cities={myCityNames} />

      {/* Total 3-year budget envelope — decorates each ResultCard with a
          fits-budget chip so cost-anxious families can scan without doing
          mental arithmetic. Non-destructive: order stays intact. */}
      <BudgetEnvelope
        budget={budgetEnvelope}
        onChange={setBudgetEnvelope}
        defaultValue={
          Number(profile?.total_family_budget_usd) > 0
            ? Number(profile.total_family_budget_usd)
            : BUDGET_DEFAULT
        }
        results={data?.results || []}
        format={format}
      />

      {/* Action plan + WUA+ */}
      {/* Focus banner — a specific course was requested from Quick Check */}
      {focusDegreeId && (focusRow || focusExcluded) && (
        <div className="mt-6" data-testid="focus-banner">
          {focusRow && (
            <div className="rounded-md border-2 border-[#4C1D95] bg-[#4C1D95]/5 p-4">
              <div className="label-uppercase !text-[10px] text-[#4C1D95] mb-1">
                Scoring the course you asked about
              </div>
              <div className="font-editorial text-xl">
                {focusRow.university_name} — {focusRow.degree_name}
              </div>
              <div className="text-sm text-stone-600 mt-1">
                Good news — this course passes all your must-have gates.
                See the full 5-layer scorecard in the “Best overall” tab below (auto-highlighted).
              </div>
            </div>
          )}
          {focusExcluded && (
            <div className="rounded-md border-2 border-amber-500 bg-amber-50 p-4">
              <div className="label-uppercase !text-[10px] text-amber-800 mb-1">
                Why this course didn't make your shortlist
              </div>
              <div className="font-editorial text-xl">
                {focusExcluded.university_name} — {focusExcluded.degree_name}
              </div>
              <div className="text-sm text-stone-800 mt-2">
                <span className="font-semibold">Reason:</span> {focusExcluded.reason}
              </div>
              <div className="text-sm text-stone-600 mt-1">
                Category: <span className="font-underground-sc tracking-wider">{focusExcluded.category}</span>
              </div>
              <div className="text-xs text-stone-600 mt-3">
                To score this course anyway, edit your profile to relax the failing constraint
                (budget, visa, PSW preference) and re-run the match.
              </div>
              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" onClick={onEdit}
                        data-testid="focus-edit-profile-btn">
                  Edit profile
                </Button>
                <Button variant="ghost" size="sm"
                        onClick={() => setActiveTab("excluded")}
                        data-testid="focus-see-excluded-btn"
                        className="text-[#4C1D95]">
                  See all exclusions →
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 border-stone-200 bg-white">
          <div className="label-uppercase flex items-center gap-2"><Sparkles className="h-3 w-3" /> Your three priorities now</div>
          <ol className="mt-4 space-y-3">
            {data.action_plan.map((p, i) => (
              <li key={i} data-testid={`priority-${i}`} className="flex gap-3">
                <span className="font-chip text-2xl font-black text-[#4C1D95] leading-none">{i + 1}</span>
                <span className="text-stone-800 leading-snug">{p}</span>
              </li>
            ))}
          </ol>
        </Card>
        {data.wua_plus_triggers.length > 0 && (
          <Card className="p-6 border-stone-200 bg-[#F3F1EC]">
            <div className="label-uppercase flex items-center gap-2 text-[#4C1D95]"><ShieldAlert className="h-3 w-3" /> WUA+ recommended</div>
            <p className="text-xs text-stone-600 mt-2">Complexity detected — a human adviser will save you time.</p>
            <ul className="mt-3 space-y-2 text-sm">
              {data.wua_plus_triggers.map((t) => (
                <li key={t} data-testid="wua-plus-trigger" className="flex gap-2 text-stone-800"><AlertTriangle className="h-4 w-4 mt-0.5 text-[#4C1D95] shrink-0" />{t}</li>
              ))}
            </ul>
            <Button className="mt-4 w-full bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-md"
                    data-testid="wua-plus-cta">
              Talk to a WUA+ adviser <ExternalLink className="h-3 w-3 ml-1" />
            </Button>
          </Card>
        )}
      </div>

      {/* Views */}
      <div className="mt-10">
        {(fromCache || data.results.length > 0) && (
          <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
            {fromCache ? (
              <div
                data-testid="advanced-results-cached-banner"
                className="flex items-center gap-2 rounded-md border border-stone-200 bg-[#F5F3FF] px-3 py-2 text-xs font-underground-sc tracking-wider text-stone-600"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#4C1D95]" />
                Showing your last match — profile unchanged
                <button
                  type="button"
                  data-testid="advanced-results-refresh-btn"
                  onClick={() => load({ force: true })}
                  className="ml-2 text-[#4C1D95] hover:underline underline-offset-4"
                >
                  Re-run match →
                </button>
              </div>
            ) : <span />}
            <RefineBar
              main={filterDefs.filter((f) => f.group === "main")}
              more={filterDefs.filter((f) => f.group === "more")}
              regions={regionOptions}
              region={regionFilter}
              onRegion={setRegionFilter}
              shown={visibleResultsRaw.length}
              total={data.results.length}
              anyActive={anyRefineActive}
              onClearAll={clearAllRefine}
              extras={<SubjectFilter options={subjectOptions} value={subjectFilter} onChange={setSubjectFilter} />}
              moreExtras={<InstitutionTypeFilter value={instFilter} onChange={setInstFilter} />}
            />
            {sortMode === "match" && (
              <p data-testid="adv-tie-order-note" className="mt-2 text-[12px] text-stone-500">
                Courses with the same overall match are ordered by Academic score, then grade gap, then university and course name A → Z.
                {groupByUni && " When grouped, a course you've shortlisted leads its university if it exactly ties the top match."}
              </p>
            )}
            {(data.summary?.postgraduate_left_out > 0 || data.summary?.duplicates_merged > 0) && (
              <p data-testid="adv-cleanup-note" className="mt-2 text-[12px] text-stone-500">
                {data.summary.postgraduate_left_out > 0 && `${data.summary.postgraduate_left_out} postgraduate course${data.summary.postgraduate_left_out === 1 ? "" : "s"} (MPhil, MSc, MBA, LLM, PhD…) left out, because Advanced Search covers undergraduate entry. `}
                {data.summary.duplicates_merged > 0 && `${data.summary.duplicates_merged} duplicate course record${data.summary.duplicates_merged === 1 ? "" : "s"} shown once (see "Also listed as" on the card).`}
              </p>
            )}
          </div>
        )}

        {/* Weight tuner — instant client-side re-rank across all 6 layers.
            Collapsed by default so it doesn't clutter first impressions. */}
        <div className="mt-6 mb-4 border border-stone-200 rounded-lg bg-white overflow-hidden" data-testid="weight-tuner">
          <button
            type="button"
            data-testid="weight-tuner-toggle"
            onClick={() => setTunerOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-stone-50"
          >
            <div className="flex items-center gap-2">
              <Sliders className="h-4 w-4 text-[#4C1D95]" />
              <span className="font-mono text-xs uppercase tracking-wider text-stone-700">
                {isDefaultWeights ? "Tune what matters to me" : "Custom weights active · re-ranked"}
              </span>
            </div>
            <span className="text-xs text-stone-500">{tunerOpen ? "hide" : "show"}</span>
          </button>
          {tunerOpen && (
            <div className="px-4 pb-5 pt-1 space-y-4 border-t border-stone-100">
              <p className="text-xs text-stone-500 leading-relaxed">
                Drag any slider 0-10 to change how much that layer matters to you. Ordering updates instantly — nothing is saved until you edit your profile.
              </p>
              {[
                { k: "academic", label: "Academic fit" },
                { k: "financial", label: "Financial fit" },
                { k: "visa", label: "Visa readiness" },
                { k: "personal", label: "Personal preferences" },
                { k: "career", label: "Career pipeline" },
                { k: "course_dna", label: "Course DNA" },
              ].map(({ k, label }) => (
                <div key={k}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-stone-700">{label}</span>
                    <span className="font-mono text-[#4C1D95]">
                      {(layerWeights[k] / (Object.values(layerWeights).reduce((a, b) => a + Math.max(0, b), 0) || 1) * 100).toFixed(0)}%
                    </span>
                  </div>
                  <Slider
                    data-testid={`weight-slider-${k}`}
                    min={0}
                    max={30}
                    step={1}
                    value={[layerWeights[k]]}
                    onValueChange={(v) => setLayerWeights((w) => ({ ...w, [k]: v[0] }))}
                    className="mt-1"
                  />
                </div>
              ))}
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="weight-tuner-reset"
                  onClick={() => setLayerWeights(DEFAULT_LAYER_WEIGHTS)}
                  disabled={isDefaultWeights}
                >
                  Reset to editor's blend
                </Button>
              </div>
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Three primary views, then the existing secondary views; wraps rather than scrolls on mobile. */}
          <TabsList aria-label="Result views" className="bg-transparent p-0 h-auto border-b border-stone-200 rounded-none w-full flex flex-wrap justify-start items-end gap-x-2 gap-y-1">
            {[["best", "Best overall"], ...(showShortlistTab ? [["shortlist", `My shortlist (${shortlistCount})`]] : []), ["portfolio", "Balanced portfolio"]].map(([v, label]) => (
              <TabsTrigger key={v} value={v} data-testid={`view-${v}`} className={`${TAB_CLS} text-[15px] font-semibold`}>{label}</TabsTrigger>
            ))}
            <span aria-hidden="true" className="hidden sm:inline-block self-center h-5 w-px bg-stone-300 mx-2" />
            <span className="basis-full sm:basis-auto self-center text-[10px] font-underground-sc tracking-wider text-stone-500 pt-1 sm:pt-0">More views</span>
            {[["lowest-risk", "Lowest visa risk"], ["best-career", "Best career return"], ["excluded", `Excluded (${excludedForSubject.length})`]].map(([v, label]) => (
              <TabsTrigger key={v} value={v} data-testid={`view-${v}`} className={`${TAB_CLS} text-[13px] text-stone-600`}>{label}</TabsTrigger>
            ))}
          </TabsList>

          {showShortlistTab && (
            <TabsContent value="shortlist" className="pt-6" data-testid="tab-shortlist">
              {assess && !assessLoading && !assessError && !(assess.items || []).length ? (
                <div data-testid="adv-shortlist-empty" className="rounded-xl border border-stone-200 bg-white p-6 text-sm text-stone-600">
                  You haven't shortlisted any courses yet. Use <strong>Add to shortlist</strong> on any course in Best overall, or in Quick Check.
                </div>
              ) : (
                <ShortlistSection
                  assess={assess}
                  loading={assessLoading}
                  error={assessError}
                  onRetry={refreshShortlist}
                  onEditProfile={onEdit}
                  onRemove={removeFromShortlist}
                  hiddenReason={shortlistHiddenReason}
                  renderCard={(row) => (
                    <ResultCard r={row} format={format} saved={savedIds.has(row.degree_id)} onAddToShortlist={addToShortlist}
                      satisfaction={satisfactionMap[row.degree_id]} onEditProfile={onEdit} budgetEnvelope={budgetEnvelope}
                      dreamEmployers={profile?.dream_employers || []} allUniIds={allUniIds} testIdPrefix="shortlist-card" />
                  )}
                />
              )}
            </TabsContent>
          )}

          <TabsContent value="best" className="pt-6">
            {data.results.length === 0 ? (
              <div className="border border-stone-200 bg-white rounded-xl p-8 text-center" data-testid="best-overall-empty">
                <div className="inline-flex items-center gap-2 label-uppercase text-[#4C1D95]">
                  <AlertTriangle className="h-4 w-4" /> No degrees match right now
                </div>
                <h3 className="font-editorial text-3xl mt-3 leading-tight">Every reviewed degree was filtered out.</h3>
                <p className="mt-3 text-stone-600 max-w-2xl mx-auto text-sm leading-relaxed">
                  This isn't the end — it means your current profile combined with the degrees we've seeded so far
                  don't intersect. The <strong>Excluded tab below</strong> shows the exact reason for every degree.
                  Most students in this position fix it with one of three edits:
                </p>
                <ul className="mt-4 space-y-2 text-sm text-stone-700 max-w-xl mx-auto text-left">
                  {data.summary.exclusion_counts?.subject_not_offered > 0 && (
                    <li className="flex gap-2" data-testid="empty-hint-subject">
                      <CheckCircle2 className="h-4 w-4 text-[#4C1D95] mt-0.5 shrink-0" />
                      <span><strong>{data.summary.exclusion_counts.subject_not_offered} degree(s)</strong> filtered on subject — try broadening your intended degree (e.g. "Philosophy" → "Humanities" or add adjacent subjects like "Politics").</span>
                    </li>
                  )}
                  {data.summary.exclusion_counts?.gate > 0 && (
                    <li className="flex gap-2" data-testid="empty-hint-gate">
                      <CheckCircle2 className="h-4 w-4 text-[#4C1D95] mt-0.5 shrink-0" />
                      <span><strong>{data.summary.exclusion_counts.gate} degree(s)</strong> hit a visa / financial gate — see the Excluded tab for the exact rule (dependants, budget, or evidence).</span>
                    </li>
                  )}
                  {data.summary.exclusion_counts?.no_sponsor_licence > 0 && (
                    <li className="flex gap-2" data-testid="empty-hint-licence">
                      <CheckCircle2 className="h-4 w-4 text-[#4C1D95] mt-0.5 shrink-0" />
                      <span><strong>{data.summary.exclusion_counts.no_sponsor_licence} university/universities</strong> don't hold an international sponsor licence — nothing you can change there.</span>
                    </li>
                  )}
                </ul>
                <div className="mt-6 flex gap-3 justify-center">
                  <Button variant="outline" onClick={onEdit} data-testid="empty-edit-profile-btn">
                    <Pencil className="h-4 w-4 mr-1" /> Edit my profile
                  </Button>
                  <Button className="bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-md"
                          data-testid="empty-see-excluded-btn"
                          onClick={() => setActiveTab("excluded")}>
                    See exclusion reasons ({data.summary.excluded})
                  </Button>
                </div>
              </div>
            ) : (() => {
              // Target-first layout — when the user came here via a
              // "See in Advanced" link from Quick Check / degree pages,
              // `focusDegreeId` is set. Instead of auto-scrolling into
              // a giant list, we lift the target to its own hero row,
              // show up to 20 strict peers (same-subject already
              // enforced by the match engine + same country), and
              // paginate the rest below.
              // "#X of Y" is about match to requirements, so rank in match order whatever the sort.
              const rankBase = isDefaultWeights ? visibleResultsRaw : visibleResults;
              const focusIdx = focusDegreeId
                ? rankBase.findIndex((r) => r.degree_id === focusDegreeId)
                : -1;

              if (focusIdx > -1) {
                const target = rankBase[focusIdx];
                const rest = visibleResults.filter((r) => r.degree_id !== focusDegreeId);
                // Strict peers: same country as the target, keep native
                // best-fit sort (`data.results` is already ranked by
                // overall_match). Fallback to global rest when the
                // country cohort is thin.
                const sameCountry = rest.filter((r) => r.country === target.country);
                const peers = (sameCountry.length >= 6 ? sameCountry : rest);
                const peersShown = peers.slice(0, visibleCounts.best);
                const peerRemaining = peers.length - peersShown.length;
                const totalMatches = visibleResults.length;
                const targetRank = focusIdx + 1;
                return (
                  <>
                    {/* Ranking context banner — preserves the "you are #X of Y"
                        signal that a flat list gave for free, but as a
                        readable sentence rather than a cryptic pill. */}
                    {(() => {
                      const higher = targetRank - 1;
                      const lower = totalMatches - targetRank;
                      let sentence;
                      if (higher === 0) {
                        sentence = `This is the strongest match against your Advanced Search requirements — ${lower} other course${lower === 1 ? "" : "s"} ranked below it.`;
                      } else if (lower === 0) {
                        sentence = `Every other course ranked above this one — ${higher} in total. Consider the peers listed below before applying.`;
                      } else {
                        sentence = `There ${higher === 1 ? "is" : "are"} ${higher} school${higher === 1 ? "" : "s"} ranked higher in terms of your requirements. See below this course for what they are.`;
                      }
                      return (
                        <div
                          data-testid="target-rank-banner"
                          className="mb-4 flex items-start gap-3 rounded-lg border border-[#4C1D95]/25 bg-[#F5F0FF] px-4 py-3"
                        >
                          <span className="mt-0.5 inline-flex items-center gap-1 shrink-0 rounded-full bg-[#4C1D95] text-white px-2 py-0.5 text-[10px] font-underground-sc tracking-wider">
                            Rank {targetRank}/{totalMatches}
                          </span>
                          <p className="text-sm text-[#3B0764] leading-snug">
                            {sentence}
                          </p>
                        </div>
                      );
                    })()}

                    {/* Target hero — full-width single card so it can't
                        get lost in a two-up grid. */}
                    <div className="mb-8">
                      <div className="text-[10px] font-underground-sc tracking-wider text-[#4C1D95] mb-2">
                        Your target course
                      </div>
                      <div data-testid="target-hero-wrapper">
                        <ResultCard
                          r={target}
                          format={format}
                          isFocused
                          saved={savedIds.has(target.degree_id)}
                          onAddToShortlist={addToShortlist}
                          satisfaction={satisfactionMap[target.degree_id]}
                          onEditProfile={onEdit}
                          budgetEnvelope={budgetEnvelope} dreamEmployers={profile?.dream_employers || []}
                        allUniIds={allUniIds} />
                      </div>
                    </div>

                    {/* Strict peer set — same country, next-best-fit. */}
                    {peers.length > 0 && (
                      <>
                        <div className="mb-3">
                          <div className="label-uppercase">
                            Closest peers in {target.country}
                          </div>
                          <div className="mt-1 text-xs text-stone-500 italic leading-snug">
                            Courses listed <em>in order</em> of how well they fit your requirements. Most targeted first.
                          </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-6" data-testid="peer-grid">
                          {peersShown.map((r) => (
                            <ResultCard
                              key={r.degree_id}
                              r={r}
                              format={format}
                              saved={savedIds.has(r.degree_id)}
                              onAddToShortlist={addToShortlist}
                              satisfaction={satisfactionMap[r.degree_id]}
                              onEditProfile={onEdit}
                              budgetEnvelope={budgetEnvelope} dreamEmployers={profile?.dream_employers || []}
                            allUniIds={allUniIds} />
                          ))}
                        </div>
                        {peerRemaining > 0 && (
                          <div className="mt-6 flex justify-center">
                            <Button
                              variant="outline"
                              data-testid="show-more-peers"
                              onClick={() => showMore("best")}
                              className="rounded-full px-6"
                            >
                              Show {Math.min(PAGE_SIZE, peerRemaining)} more · {peerRemaining} still hidden
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </>
                );
              }

              // No target set — original "top N + Show more" behaviour.
              const { node, remaining, unit, unitPlural } = renderGroupedResultCards(
                visibleResults,
                "best",
                visibleCounts.best
              );
              return (
                <>
                  {node}
                  {remaining > 0 && (
                    <div className="mt-6 flex justify-center">
                      <Button
                        variant="outline"
                        data-testid="show-more-best"
                        onClick={() => showMore("best")}
                        className="rounded-full px-6"
                      >
                        Show {Math.min(PAGE_SIZE, remaining)} more · {remaining} {remaining === 1 ? unit : unitPlural} still hidden
                      </Button>
                    </div>
                  )}
                </>
              );
            })()}
            {visibleResults.length > 0 && (
              <div className="mt-8">
                <PlusCTA
                  variant="inline"
                  testId="advresults-plus-cta"
                  headline="Want a human eye on these matches?"
                  body="Your 5-layer match is honest — but Ivy strategy, ED/EA timing and personal-statement narrative benefit from a counsellor who's seen it before. WUA+ is where that happens."
                  primaryLabel="See WUA+"
                />
              </div>
            )}
          </TabsContent>

          <TabsContent value="portfolio" className="pt-6">
            <div className="mb-4">
              <ChanceLegend testId="advresults-chance-legend" defaultOpen={true} />
            </div>
            <PortfolioExplainer results={data.results || []} profile={profile} subjects={subjectOptions} onEdit={onEdit}
              shortlist={showShortlistTab ? assess : null} />
            {(() => {
              // Budget-aware portfolio biasing. For each slot we try to
              // guarantee at least one pick sits inside the user's
              // envelope. When we're forced to re-shuffle, we tag the
              // slot with a small "budget-aware" chip so it's transparent.
              const SLOT_SIZES = { aspirational: 2, competitive: 3, realistic: 3, secure: 2 };
              const slotOverrides = {};
              const results = data.results || [];
              const inEnvelope = (r) => {
                const c = Number(r.financial?.net_degree_cost_usd ?? r.financial?.total_degree_usd ?? 0);
                return c > 0 && c <= budgetEnvelope;
              };
              for (const slot of Object.keys(SLOT_SIZES)) {
                const size = SLOT_SIZES[slot];
                // Server's default pick for this slot.
                const defaultIds = (data.balanced_portfolio?.[slot] || []).slice();
                const defaultAffordable = defaultIds.some((id) => byId[id] && inEnvelope(byId[id]));
                if (defaultAffordable) {
                  slotOverrides[slot] = { ids: defaultIds, mutated: false, note: null };
                  continue;
                }
                // Look for the best-match affordable degree in this
                // academic slot from the full result set.
                const candidates = results
                  .filter((r) => r.academic?.portfolio_slot === slot && inEnvelope(r))
                  .sort((a, b) => b.overall_match - a.overall_match);
                if (candidates.length === 0) {
                  slotOverrides[slot] = { ids: defaultIds, mutated: false,
                    note: "No pick in this tier fits the budget — try widening the envelope." };
                  continue;
                }
                const swapIn = candidates[0].degree_id;
                // Drop the weakest default and inject the affordable pick.
                const kept = defaultIds.filter((id) => id !== swapIn);
                const nextIds = [swapIn, ...kept].slice(0, size);
                slotOverrides[slot] = { ids: nextIds, mutated: true,
                  note: `1 pick swapped in to keep you inside the ${format(budgetEnvelope)} envelope.` };
              }
              return ["aspirational", "competitive", "realistic", "secure"].map((slot) => {
                const { ids, mutated, note } = slotOverrides[slot];
                const shownIds = ids.filter((id) => byId[id] && !(hideNC && isNC(byId[id])));
                const extraShortlisted = showShortlistTab ? shortlistedInSlot(assess, slot, shownIds) : [];
                return (
                  <div key={slot} className="mb-8" data-testid={`portfolio-slot-${slot}`}>
                    <div className="flex flex-wrap items-baseline gap-2 mb-3">
                      <SlotHeader slot={slot} size={SLOT_SIZES[slot]} filled={shownIds.length} />
                      {mutated && (
                        <span data-testid={`portfolio-slot-budget-aware-${slot}`}
                              className="inline-flex items-center gap-1 text-[9px] font-underground-sc tracking-wider bg-[#4C1D95]/10 text-[#4C1D95] border border-[#4C1D95]/25 rounded-full px-2 py-0.5">
                          Budget-aware
                        </span>
                      )}
                    </div>
                    {note && (
                      <div className="text-[11px] italic text-stone-500 mb-3 leading-snug"
                           data-testid={`portfolio-slot-note-${slot}`}>
                        {note}
                      </div>
                    )}
                    <div className="grid md:grid-cols-2 gap-6">
                      {shownIds.map((id) => (
                        <div key={id} className="min-w-0">
                          {savedIds.has(id) && <ShortlistedTag testId={`portfolio-shortlisted-tag-${id}`} />}
                          <ResultCard r={byId[id]} format={format} saved={savedIds.has(id)} onAddToShortlist={addToShortlist} satisfaction={satisfactionMap[id]} onEditProfile={onEdit} budgetEnvelope={budgetEnvelope} dreamEmployers={profile?.dream_employers || []} allUniIds={allUniIds} />
                        </div>
                      ))}
                    </div>
                    {shownIds.length === 0 && (
                      <SlotEmptyReason slot={slot} results={data.results || []} profile={profile} onEdit={onEdit}
                        onProfileSaved={user && !anonymous ? onProfileSaved : undefined} />
                    )}
                    {extraShortlisted.length > 0 && (
                      <div className="mt-5" data-testid={`portfolio-slot-shortlisted-${slot}`}>
                        <div className="text-[11px] font-underground-sc tracking-wider text-[#4C1D95] mb-2">
                          Your shortlisted courses in this tier · {extraShortlisted.length} · not counted in the {SLOT_SIZES[slot]} slots
                        </div>
                        <div className="grid md:grid-cols-2 gap-6">
                          {extraShortlisted.map((row) => (
                            <div key={row.degree_id} className="min-w-0">
                              <ShortlistedTag testId={`portfolio-shortlisted-tag-${row.degree_id}`} />
                              <ResultCard r={byId[row.degree_id] || row} format={format} saved onAddToShortlist={addToShortlist} satisfaction={satisfactionMap[row.degree_id]} onEditProfile={onEdit} budgetEnvelope={budgetEnvelope} dreamEmployers={profile?.dream_employers || []} allUniIds={allUniIds} testIdPrefix="portfolio-shortlist-card" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </TabsContent>

          <TabsContent value="lowest-risk" className="pt-6">
            {(() => {
              const sorted = [...visibleResults].sort((a, b) => b.visa.score - a.visa.score);
              const { node, remaining, unit, unitPlural } = renderGroupedResultCards(
                sorted,
                "lowest-risk",
                visibleCounts["lowest-risk"]
              );
              return (
                <>
                  {node}
                  {remaining > 0 && (
                    <div className="mt-6 flex justify-center">
                      <Button variant="outline" data-testid="show-more-lowest-risk" onClick={() => showMore("lowest-risk")} className="rounded-full px-6">
                        Show {Math.min(PAGE_SIZE, remaining)} more · {remaining} {remaining === 1 ? unit : unitPlural} still hidden
                      </Button>
                    </div>
                  )}
                </>
              );
            })()}
          </TabsContent>

          <TabsContent value="best-career" className="pt-6">
            {(() => {
              const sorted = [...visibleResults].sort((a, b) => b.career.score - a.career.score);
              const { node, remaining, unit, unitPlural } = renderGroupedResultCards(
                sorted,
                "best-career",
                visibleCounts["best-career"]
              );
              return (
                <>
                  {node}
                  {remaining > 0 && (
                    <div className="mt-6 flex justify-center">
                      <Button variant="outline" data-testid="show-more-best-career" onClick={() => showMore("best-career")} className="rounded-full px-6">
                        Show {Math.min(PAGE_SIZE, remaining)} more · {remaining} {remaining === 1 ? unit : unitPlural} still hidden
                      </Button>
                    </div>
                  )}
                </>
              );
            })()}
          </TabsContent>

          <TabsContent value="excluded" className="pt-6">
            {excludedForSubject.length === 0 ? (
              <div className="text-sm text-stone-500">Nothing excluded — every reviewed degree passed the gates.</div>
            ) : (() => {
              // Build the reason chip list dynamically from the excluded rows.
              // Split `gate` into Financial/Visa/Both using the `gate_type`
              // field the backend now emits.
              const rowKey = (e) => {
                if (e.category !== "gate") return e.category;
                if (e.gate_type === "financial") return "financial_gate";
                if (e.gate_type === "visa") return "visa_gate";
                return "visa_and_financial_gate";
              };
              const LABELS = {
                subject_not_offered: "Subject not offered",
                country_not_selected: "Country not selected",
                no_sponsor_licence: "No sponsor licence",
                financial_gate: "Financial",
                visa_gate: "Visa",
                visa_and_financial_gate: "Visa + financial",
              };
              const counts = {};
              excludedForSubject.forEach((e) => {
                const k = rowKey(e);
                counts[k] = (counts[k] || 0) + 1;
              });
              // Sort chips by count desc so most common reason is leftmost.
              const chipKeys = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
              const filtered = excludedFilter === "all"
                ? excludedForSubject
                : excludedForSubject.filter((e) => rowKey(e) === excludedFilter);
              return (
                <>
                  <div className="mb-4 flex flex-wrap gap-2 items-center"
                       data-testid="excluded-filter-chips">
                    <span className="label-uppercase !text-[10px] text-stone-500 mr-1">Filter by reason:</span>
                    <button type="button"
                            data-testid="excluded-filter-all"
                            onClick={() => { setExcludedFilter("all"); setVisibleCounts((c) => ({ ...c, excluded: PAGE_SIZE })); }}
                            className={`px-3 py-1 rounded-full text-xs font-underground-sc tracking-wider border transition
                              ${excludedFilter === "all"
                                ? "bg-stone-900 text-white border-stone-900"
                                : "bg-white border-stone-300 text-stone-700 hover:border-stone-500"}`}>
                      All · {excludedForSubject.length}
                    </button>
                    {chipKeys.map((k) => (
                      <button key={k}
                              type="button"
                              data-testid={`excluded-filter-${k}`}
                              onClick={() => { setExcludedFilter(k); setVisibleCounts((c) => ({ ...c, excluded: PAGE_SIZE })); }}
                              className={`px-3 py-1 rounded-full text-xs font-underground-sc tracking-wider border transition
                                ${excludedFilter === k
                                  ? "bg-stone-900 text-white border-stone-900"
                                  : "bg-white border-stone-300 text-stone-700 hover:border-stone-500"}`}>
                        {LABELS[k] || k} · {counts[k]}
                      </button>
                    ))}
                  </div>
                  <div className="text-xs text-stone-500 mb-3" data-testid="excluded-filter-count">
                    Showing {filtered.length} of {excludedForSubject.length} excluded course{filtered.length === 1 ? "" : "s"}
                    {excludedFilter !== "all" && <> filtered on <span className="font-underground-sc">{LABELS[excludedFilter] || excludedFilter}</span></>}.
                  </div>
                  {filtered.length === 0 ? (
                    <div className="text-sm text-stone-500 border border-dashed border-stone-300 rounded-md p-6 text-center">
                      No excluded courses in this category.
                    </div>
                  ) : (() => {
                    const shownExcluded = filtered.slice(0, visibleCounts.excluded);
                    const remainingExcluded = filtered.length - shownExcluded.length;
                    return (
                      <>
                        <div className="space-y-3">
                          {shownExcluded.map((e) => (
                            <div key={e.degree_id || e.university_id}
                                 data-testid={`excluded-row-${e.degree_id || e.university_id}`}
                                 data-focus-row={e.degree_id === focusDegreeId ? e.degree_id : undefined}
                                 className={`border-l-4 p-4 rounded-md ${e.degree_id === focusDegreeId ? "border-amber-500 bg-amber-50 ring-2 ring-amber-300" : "border-stone-700 bg-stone-100"}`}>
                              <div className="font-editorial text-lg">{e.university_name}{e.degree_name ? ` — ${e.degree_name}` : ""}</div>
                              <div className="text-sm text-stone-700 mt-1">{e.reason}</div>
                              <div className="label-uppercase !text-[9px] mt-2">
                                Not currently feasible · {LABELS[rowKey(e)] || e.category}
                              </div>
                            </div>
                          ))}
                        </div>
                        {remainingExcluded > 0 && (
                          <div className="mt-6 flex justify-center">
                            <Button variant="outline" data-testid="show-more-excluded" onClick={() => showMore("excluded")} className="rounded-full px-6">
                              Show {Math.min(PAGE_SIZE, remainingExcluded)} more · {remainingExcluded} still hidden
                            </Button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              );
            })()}
          </TabsContent>
        </Tabs>

        {/* Directory-tier "Also available" list — quieter than editorial
            matches; funnels users to per-uni pages for the full course
            catalogue. Uses profile.intended_degree + preferred country. */}
        <DirectoryMatchesSection
          subject={profile?.intended_degree || ""}
          country={(profile?.preferred_countries || [])[0] || null}
        />
      </div>

      {(data.summary?.recommended || 0) >= 3 && (
        <div className="mt-10 print:hidden">
          <PlusReportCompactStrip
            testId="advanced-results-plus-report-strip"
            hook={`Advanced Search found ${data.summary.recommended} matches. A WUA Personalised Report ranks them, flags financial gaps and sets your application timeline — in 60 seconds.`}
          />
        </div>
      )}
    </div>
    </MyCitiesContext.Provider>
    </SortModeContext.Provider>
  );
}
